import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Tooltip,
  useTheme,
  FormControlLabel,
  Checkbox,
  TextareaAutosize,
  Badge,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  InputAdornment,
  Grid,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Radio,
  RadioGroup,
} from '@mui/material';
import { theme as appTheme } from '../../styles/theme';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  CheckCircle as ApproveIcon,
  Edit as EditIcon,
  History as HistoryIcon,
  Flag as FlagIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Person as PersonIcon,
  CalendarToday as DateIcon,
  Description as ReportIcon,
  LocalHospital as LabIcon,
  TrendingUp,
  TrendingDown,
  Remove as NormalIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Code as CodeIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  AddCircle as AddIcon,
  Block as BlockIcon,
  LibraryAdd as ParameterMasterIcon,
  Publish as PublishIcon,
} from '@mui/icons-material';
import api from '../../services/api';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import { Report, TestParameter, TestResult, EditHistory } from '../../types';
import ReportPdfViewer from '../../components/common/ReportPdfViewer';
import { canReprocessReport } from '../../utils/permissions';
import { readNumber } from '../../utils/numberText';
import { resultLabel } from '../../utils/resultLabel';
import {
  displayedRows,
  editFormFromRow,
  mappingRowRef,
  reviewRowRef,
  rowTargetMessage,
  rowUpdates,
} from '../../utils/reviewRowEdit';
import BypassCsvPanel from '../../components/common/BypassCsvPanel';
import ValidationFlag from '../../components/validation/ValidationFlag';
import MappingStatusChip, { VendorMapping } from '../../components/validation/MappingStatusChip';
import ParameterSearchField from '../../components/parameters/ParameterSearchField';

import JsonOutputView from '../../components/JsonOutputView';
// Using EditHistory from types

// Interface for review session tracking
interface ReviewSession {
  reportId: string;
  accumulatedSeconds: number;  // Total time across all sessions
  sessionStartTime: number | null;  // Current session start timestamp
  lastSavedTime: number;  // When was this last updated
}

// localStorage helper functions for review session management
const STORAGE_PREFIX = 'review_session_';

const getReviewSession = (reportId: string): ReviewSession | null => {
  try {
    const key = `${STORAGE_PREFIX}${reportId}`;
    const data = localStorage.getItem(key);
    if (data) {
      return JSON.parse(data) as ReviewSession;
    }
  } catch (error) {
    console.error('[REVIEW TIMER] Error reading session from localStorage:', error);
  }
  return null;
};

const saveReviewSession = (session: ReviewSession): void => {
  try {
    const key = `${STORAGE_PREFIX}${session.reportId}`;
    localStorage.setItem(key, JSON.stringify(session));
  } catch (error) {
    console.error('[REVIEW TIMER] Error saving session to localStorage:', error);
  }
};

const removeReviewSession = (reportId: string): void => {
  try {
    const key = `${STORAGE_PREFIX}${reportId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error('[REVIEW TIMER] Error removing session from localStorage:', error);
  }
};

// Clean up old sessions (older than 7 days)
const cleanupOldSessions = (): void => {
  try {
    const keys = Object.keys(localStorage);
    const now = Date.now();
    const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;

    keys.forEach(key => {
      if (key.startsWith(STORAGE_PREFIX)) {
        try {
          const session = JSON.parse(localStorage.getItem(key) || '{}') as ReviewSession;
          if (session.lastSavedTime && (now - session.lastSavedTime > SEVEN_DAYS)) {
            localStorage.removeItem(key);
            console.log('[REVIEW TIMER] Cleaned up old session:', key);
          }
        } catch (e) {
          // Remove corrupted entries
          localStorage.removeItem(key);
        }
      }
    });
  } catch (error) {
    console.error('[REVIEW TIMER] Error during cleanup:', error);
  }
};

const ReviewReport: React.FC = () => {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { isAdmin, user } = useAuth();

  const [report, setReport] = useState<Report | null>(null);
  const resultIndexById = useMemo(() => {
    const map = new Map<string, number>();
    (report?.extractedData?.results || []).forEach((r: any, idx: number) => {
      if (r && r._id) map.set(String(r._id), idx);
    });
    return map;
  }, [report?.extractedData?.results]);
  // pageWiseData rows have a different shape that omits vendorMapping. Build a lookup so
  // per-page rendering can still surface the mapping chips (which live on the top-level
  // results array). We index by BOTH the row's current serviceItemName AND its
  // originalExtracted name: the matcher's method-strip tier rewrites serviceItemName on the
  // top-level row (e.g. "Neutrophils(Flow Cytometry)" -> "Neutrophils") while pageWiseData
  // keeps the original, so an originalExtracted-keyed entry is what lets the page-wise row
  // find its mapping.
  const vendorMappingByKey = useMemo(() => {
    const map = new Map<string, { mapping: any; resultIndex: number }>();
    (report?.extractedData?.results || []).forEach((r: any, idx: number) => {
      if (!r) return;
      const value = r.value ?? '';
      const entry = { mapping: r.vendorMapping, resultIndex: idx };
      const keys = [];
      if (r.vendorMapping?.originalExtracted) keys.push(`${r.vendorMapping.originalExtracted}|${value}`);
      if (r.serviceItemName) keys.push(`${r.serviceItemName}|${value}`);
      for (const key of keys) {
        if (!map.has(key)) map.set(key, entry);
      }
    });
    return map;
  }, [report?.extractedData?.results]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState<{ [key: string]: boolean }>({});
  const [editedValues, setEditedValues] = useState<{ [key: string]: any }>({});
  const [showHistory, setShowHistory] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});
  const [editingOrderId, setEditingOrderId] = useState(false);
  const [tempOrderId, setTempOrderId] = useState('');
  const [savingOrderId, setSavingOrderId] = useState(false);
  const [showRepeatReview, setShowRepeatReview] = useState(false);
  const [showJsonOutput, setShowJsonOutput] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  // Cumulative time tracking state
  const [currentSessionStart, setCurrentSessionStart] = useState<number | null>(null);
  const [accumulatedTime, setAccumulatedTime] = useState<number>(0);
  const [isTimerActive, setIsTimerActive] = useState<boolean>(false);
  const [parameterToDelete, setParameterToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [validationWarnings, setValidationWarnings] = useState<any[]>([]);
  const [mismatchReason, setMismatchReason] = useState('');
  const [orderData, setOrderData] = useState<any>(null);
  const [reportData, setReportData] = useState<any>(null);
  const [hasValidationWarnings, setHasValidationWarnings] = useState(false);
  const [loadingValidation, setLoadingValidation] = useState(false);
  const [revalidating, setRevalidating] = useState(false);
  const [showParameterMasterDialog, setShowParameterMasterDialog] = useState(false);
  const [showExclusionDialog, setShowExclusionDialog] = useState(false);
  const [selectedParameter, setSelectedParameter] = useState<TestResult | null>(null);
  const [showExcludedParamsDialog, setShowExcludedParamsDialog] = useState(false);
  const [selectedValidationFlag, setSelectedValidationFlag] = useState<any | null>(null);
  // Parameter Master dialog state
  const [parameterAction, setParameterAction] = useState<'create' | 'update_alias'>('create');
  const [targetParameter, setTargetParameter] = useState<any | null>(null);
  const [newParameterId, setNewParameterId] = useState('');
  const [valueType, setValueType] = useState<'numeric' | 'text' | 'alphanumeric' | 'range'>('numeric');
  const [parameterDescription, setParameterDescription] = useState('');
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isPublished, setIsPublished] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  // Re-publish (fix & re-send an already-published record)
  const [showRepublishDialog, setShowRepublishDialog] = useState(false);
  const [republishPreview, setRepublishPreview] = useState<any>(null);
  const [isRepublishing, setIsRepublishing] = useState(false);
  const [republishApplied, setRepublishApplied] = useState(false);
  const [republishKeepChoices, setRepublishKeepChoices] = useState<Record<string, string>>({});
  // Phase 9: mapping gate dialog (soft confirmation when params are unmapped/unconfirmed)
  const [showMappingGateDialog, setShowMappingGateDialog] = useState(false);
  const [mappingGateData, setMappingGateData] = useState<{
    unmappedParameters: Array<{ resultIndex: number; serviceItemName: string; originalExtracted: string }>;
    unconfirmedSuggestions: Array<{ resultIndex: number; serviceItemName: string; suggestedName: string; confidence: number }>;
  }>({ unmappedParameters: [], unconfirmedSuggestions: [] });

  // Soft warning shown before publishing when the HIMS order looks unusual
  // (status not Final, or not part of a package). Informational — neither
  // blocks publishing any more; the nurse acknowledges and proceeds.
  const [orderConditionWarnings, setOrderConditionWarnings] = useState<string[]>([]);
  const [showOrderConditionsDialog, setShowOrderConditionsDialog] = useState(false);

  // Acknowledgements the nurse has ACTUALLY given during the current publish
  // attempt. /publish is stateless and re-evaluates every gate on every POST, so
  // a dialog that sends only its own flag makes an already-answered gate fire
  // again. A ref, not state: each gate re-invokes handlePublish immediately and
  // must see the previous acknowledgement without waiting for a re-render.
  // Nothing here may ever be set by a dialog the nurse did not see.
  const publishAcks = useRef<{ mapping: boolean; orderConditions: boolean }>({ mapping: false, orderConditions: false });
  // Value-confirmation gate: the same test reported with more than one value. The tool
  // never picks — the nurse checks the source PDF and confirms before anything is sent.
  const [valueConflicts, setValueConflicts] = useState<Array<{
    key: string;
    name: string;
    options: Array<{ id: string; value: string; unit: string; method: string; referenceRange: string }>;
  }> | null>(null);
  const [valueChoices, setValueChoices] = useState<Record<string, string>>({});
  // Phase 11: bulk-confirm + retrigger busy flags
  const [bulkConfirming, setBulkConfirming] = useState(false);
  const [retriggeringMapping, setRetriggeringMapping] = useState(false);
  // Lab name dropdown state
  const [configuredLabs, setConfiguredLabs] = useState<string[]>([]);
  const [savingLabName, setSavingLabName] = useState(false);
  const [editingLabName, setEditingLabName] = useState(false);
  // Reprocessing state
  const [reprocessing, setReprocessing] = useState(false);

  // Timer helper functions
  const pauseTimer = () => {
    if (!id || !isTimerActive || currentSessionStart === null) return;

    const now = Date.now();
    const sessionDuration = Math.floor((now - currentSessionStart) / 1000);
    const newAccumulated = accumulatedTime + sessionDuration;

    console.log('[REVIEW TIMER] Pausing timer:', {
      sessionDuration,
      previousAccumulated: accumulatedTime,
      newAccumulated
    });

    setAccumulatedTime(newAccumulated);
    setCurrentSessionStart(null);
    setIsTimerActive(false);

    // Save to localStorage
    saveReviewSession({
      reportId: id,
      accumulatedSeconds: newAccumulated,
      sessionStartTime: null,
      lastSavedTime: now
    });
  };

  const resumeTimer = () => {
    if (!id || isTimerActive) return;

    const now = Date.now();
    console.log('[REVIEW TIMER] Resuming timer, accumulated time:', accumulatedTime);

    setCurrentSessionStart(now);
    setIsTimerActive(true);

    // Update localStorage
    saveReviewSession({
      reportId: id,
      accumulatedSeconds: accumulatedTime,
      sessionStartTime: now,
      lastSavedTime: now
    });
  };

  const saveCurrentSession = () => {
    if (!id) return;

    let finalAccumulated = accumulatedTime;

    // If timer is active, add current session time
    if (isTimerActive && currentSessionStart !== null) {
      const now = Date.now();
      const sessionDuration = Math.floor((now - currentSessionStart) / 1000);
      finalAccumulated = accumulatedTime + sessionDuration;
    }

    console.log('[REVIEW TIMER] Saving current session, total accumulated:', finalAccumulated);

    saveReviewSession({
      reportId: id,
      accumulatedSeconds: finalAccumulated,
      sessionStartTime: isTimerActive ? Date.now() : null,
      lastSavedTime: Date.now()
    });
  };

  useEffect(() => {
    console.log('[REVIEW] 1. Component mounted, report ID from URL:', id);
    fetchReport();
  }, [id]);

  // Fetch configured lab names for dropdown
  useEffect(() => {
    const fetchConfiguredLabs = async () => {
      try {
        const response = await api.get('/reports/config/lab-names');
        if (response.data.success && response.data.data) {
          setConfiguredLabs(response.data.data);
          console.log('[REVIEW] Loaded configured labs:', response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch configured labs:', error);
      }
    };
    fetchConfiguredLabs();
  }, []);

  // Handle lab name change
  const handleLabNameChange = async (event: SelectChangeEvent<string>) => {
    if (!report || !id) return;
    const newLabName = event.target.value;
    if (newLabName === report.extractedData?.labName) {
      setEditingLabName(false);
      return;
    }
    setSavingLabName(true);
    try {
      const response = await api.patch(`/reports/${id}`, {
        extractedData: {
          ...report.extractedData,
          labName: newLabName
        }
      });
      if (response.data.success) {
        setReport((prev: Report | null) => {
          if (!prev) return null;
          return {
            ...prev,
            extractedData: {
              ...prev.extractedData,
              labName: newLabName
            }
          } as Report;
        });
        enqueueSnackbar('Laboratory updated — re-running mapping…', { variant: 'info' });
        // Auto re-run mapping with the new vendor so the nurse doesn't have to click
        // the separate "Re-run Mapping" button.
        try {
          await api.post(`/review/${id}/mapping/retrigger`);
          const refreshed = await api.get(`/reports/${id}`);
          if (refreshed.data?.data) setReport(refreshed.data.data);
          enqueueSnackbar('Mapping refreshed for new lab', { variant: 'success' });
        } catch (e: any) {
          enqueueSnackbar(`Mapping retrigger failed: ${e.message || 'unknown'}`, { variant: 'warning' });
        }
      }
    } catch (error) {
      console.error('Failed to update lab name:', error);
      enqueueSnackbar('Failed to update laboratory', { variant: 'error' });
    } finally {
      setSavingLabName(false);
      setEditingLabName(false);
    }
  };

  // Page Visibility API - pause timer when tab becomes inactive
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('[REVIEW TIMER] Page became hidden, pausing timer');
        pauseTimer();
      } else {
        console.log('[REVIEW TIMER] Page became visible, resuming timer');
        resumeTimer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isTimerActive, currentSessionStart, accumulatedTime, id]);

  // beforeunload - save session before page unload/navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log('[REVIEW TIMER] Page unloading, saving session');
      saveCurrentSession();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isTimerActive, currentSessionStart, accumulatedTime, id]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      console.log('[REVIEW TIMER] Component unmounting, saving session');
      saveCurrentSession();
    };
  }, [isTimerActive, currentSessionStart, accumulatedTime, id]);

  const fetchReport = async () => {
    console.log('[REVIEW] 2. fetchReport called for ID:', id);
    try {
      setLoading(true);
      console.log('[REVIEW] 3. Fetching report from /reports/' + id);

      const response = await api.get(`/reports/${id}`);

      console.log('[REVIEW] 4. Response received:', response);
      console.log('[REVIEW] 5. Response status:', response.status);
      console.log('[REVIEW] 6. Response data:', response.data);
      console.log('[REVIEW] 7. Response success:', response.data.success);
      console.log('[REVIEW] 8. Response data.data:', response.data.data);

      if (response.data.data) {
        const reportData = response.data.data;
        console.log('[REVIEW] 9. Report data structure:', {
          _id: reportData._id,
          orderId: reportData.orderId,
          status: reportData.status,
          hasExtractedData: !!reportData.extractedData,
          extractedDataKeys: reportData.extractedData ? Object.keys(reportData.extractedData) : [],
        });

        if (reportData.extractedData) {
          console.log('[REVIEW] 10. Extracted data:', reportData.extractedData);
          console.log('[REVIEW] 11. Lab name:', reportData.extractedData.labName);
          console.log('[REVIEW] 12. Has results:', !!reportData.extractedData.results);
          console.log('[REVIEW] 13. Results count:', reportData.extractedData.results?.length || 0);
          console.log('[REVIEW] 14. Results array:', reportData.extractedData.results);

          if (reportData.extractedData.results && Array.isArray(reportData.extractedData.results)) {
            console.log('[REVIEW] 15. First 3 results:', reportData.extractedData.results.slice(0, 3));
          } else {
            console.error('[REVIEW] ERROR: Results is not an array:', reportData.extractedData.results);
          }
        } else {
          console.warn('[REVIEW] WARNING: No extractedData in report');
        }

        console.log('[REVIEW] 16. Setting report state');
        setReport(response.data.data);

        // Initialize cumulative timer for review tracking (only if status is 'ready' or 'approved')
        if (response.data.data.status === 'ready' || response.data.data.status === 'approved') {
          if (id) {
            // Clean up old sessions first
            cleanupOldSessions();

            // Check for existing session in localStorage
            const existingSession = getReviewSession(id);

            if (existingSession) {
              console.log('[REVIEW TIMER] Restoring existing session:', existingSession);
              setAccumulatedTime(existingSession.accumulatedSeconds);

              // Start new session
              const now = Date.now();
              setCurrentSessionStart(now);
              setIsTimerActive(true);

              saveReviewSession({
                reportId: id,
                accumulatedSeconds: existingSession.accumulatedSeconds,
                sessionStartTime: now,
                lastSavedTime: now
              });
            } else {
              // Start fresh session
              console.log('[REVIEW TIMER] Starting fresh review session');
              const now = Date.now();
              setAccumulatedTime(0);
              setCurrentSessionStart(now);
              setIsTimerActive(true);

              saveReviewSession({
                reportId: id,
                accumulatedSeconds: 0,
                sessionStartTime: now,
                lastSavedTime: now
              });
            }
          }
        }
      } else {
        console.error('[REVIEW] ERROR: No data in response.data.data');
      }
    } catch (error: any) {
      console.error('[REVIEW] ERROR: Failed to load report:', error);
      console.error('[REVIEW] ERROR: Response data:', error.response?.data);
      console.error('[REVIEW] ERROR: Status:', error.response?.status);
      console.error('[REVIEW] ERROR: Message:', error.message);
      enqueueSnackbar('Failed to load report', { variant: 'error' });
      navigate('/nurse/reports');
    } finally {
      setLoading(false);
      console.log('[REVIEW] 17. Loading complete');
    }
  };

  const handleEditToggle = (parameterId: string) => {
    setEditMode(prev => ({
      ...prev,
      [parameterId]: !prev[parameterId]
    }));

    if (!editMode[parameterId]) {
      // The exact row the table shows (a page row when the report has page data: its _id is not in results)
      const parameter = displayedRows(report?.extractedData, currentPage).find(p => p._id === parameterId);
      if (parameter) {
        const form = editFormFromRow(parameter);
        setEditedValues(prev => ({
          ...prev,
          [`${parameterId}_value`]: form.value,
          [`${parameterId}_unit`]: form.unit,
          [`${parameterId}_refLow`]: form.refLow,
          [`${parameterId}_refHigh`]: form.refHigh,
          [`${parameterId}_refRange`]: form.refRange,
        }));
      }
    }
  };

  const handleSaveEdit = async (parameterId: string) => {
    console.log('========== SAVE EDIT DEBUG START ==========');
    console.log('[SAVE EDIT] Function called with parameterId:', parameterId);
    console.log('[SAVE EDIT] Report ID:', id);

    if (!report) {
      console.log('[SAVE EDIT] ✗ No report available');
      return;
    }

    setSaving(true);
    try {
      // The row being shown: the same rows the table renders (page data when the report has it)
      const currentResults = displayedRows(report.extractedData, currentPage);
      const currentParameter = currentResults.find(p => p._id === parameterId);
      console.log('[SAVE EDIT] Current parameter (from displayed list):', currentParameter);

      if (!currentParameter) {
        console.log('[SAVE EDIT] ✗ Parameter not found in current results');
        console.log('[SAVE EDIT] Available parameters:', currentResults.map(p => ({ id: p._id, name: p.serviceItemName })));
        enqueueSnackbar('Parameter not found', { variant: 'error' });
        return;
      }

      // Log all edited values for this parameter
      const relevantEditedValues = {
        value: editedValues[`${parameterId}_value`],
        unit: editedValues[`${parameterId}_unit`],
        refLow: editedValues[`${parameterId}_refLow`],
        refHigh: editedValues[`${parameterId}_refHigh`],
        refRange: editedValues[`${parameterId}_refRange`]
      };
      console.log('[SAVE EDIT] Edited values:', relevantEditedValues);

      // Only what the nurse changed, compared with the row being shown: untouched fields keep their
      // stored values (a limit of 0 included); an emptied field is saved empty
      const updates: Array<{ field: string; newValue: any }> = rowUpdates(currentParameter, relevantEditedValues);

      console.log('[SAVE EDIT] Total updates to send:', updates.length);
      console.log('[SAVE EDIT] Updates array:', JSON.stringify(updates, null, 2));

      // If no changes, just exit edit mode
      if (updates.length === 0) {
        console.log('[SAVE EDIT] No changes detected, exiting edit mode');
        setEditMode(prev => ({ ...prev, [parameterId]: false }));
        enqueueSnackbar('No changes to save', { variant: 'info' });
        return;
      }

      // The exact rows to change (sent as ids): linked by rowKey, or, on older reports without a
      // link, paired by name only when that name picks out one row
      const target = reviewRowRef(report.extractedData, currentParameter, currentPage);
      if (!target.ok) {
        console.log('[SAVE EDIT] ✗ Row cannot be matched safely:', target.reason);
        enqueueSnackbar(rowTargetMessage(target.reason), { variant: 'error' });
        return;
      }
      const parameterName = target.parameterName;

      // Send each update separately
      for (let i = 0; i < updates.length; i++) {
        const update = updates[i];
        const payload = {
          parameterName: parameterName,
          ...target.ids,
          field: update.field,
          newValue: update.newValue,
          reason: 'Manual correction by nurse'
        };

        console.log(`[SAVE EDIT] Sending update ${i + 1}/${updates.length}:`, JSON.stringify(payload, null, 2));
        console.log(`[SAVE EDIT] API URL: /review/${id}/parameter`);

        try {
          const response = await api.put(`/review/${id}/parameter`, payload);
          console.log(`[SAVE EDIT] ✓ Update ${i + 1} successful:`, response.data);
        } catch (updateError: any) {
          console.error(`[SAVE EDIT] ✗ Update ${i + 1} failed:`, {
            status: updateError.response?.status,
            statusText: updateError.response?.statusText,
            data: updateError.response?.data,
            message: updateError.message,
            fullError: updateError
          });
          throw updateError; // Re-throw to be caught by outer catch
        }
      }

      console.log('[SAVE EDIT] All updates sent successfully, refreshing report...');

      // Refresh the report to get updated data
      await fetchReport();
      console.log('[SAVE EDIT] Report refreshed successfully');

      setEditMode(prev => ({ ...prev, [parameterId]: false }));
      enqueueSnackbar('Parameter updated successfully', { variant: 'success' });
      console.log('[SAVE EDIT] ✓ Save completed successfully');

    } catch (error: any) {
      console.error('[SAVE EDIT] ✗ ERROR OCCURRED:');
      console.error('[SAVE EDIT] Error type:', error.constructor.name);
      console.error('[SAVE EDIT] Error message:', error.message);
      console.error('[SAVE EDIT] Error response status:', error.response?.status);
      console.error('[SAVE EDIT] Error response data:', error.response?.data);
      console.error('[SAVE EDIT] Error response headers:', error.response?.headers);
      console.error('[SAVE EDIT] Full error object:', error);
      console.error('[SAVE EDIT] Error stack:', error.stack);

      const errorMessage = error.response?.data?.message || 'Failed to update parameter';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      console.error('Edit parameter error:', error.response?.data);
    } finally {
      setSaving(false);
      console.log('========== SAVE EDIT DEBUG END ==========');
    }
  };

  const fetchValidationData = async () => {
    try {
      setLoadingValidation(true);
      const response = await api.get(`/review/${id}/validation`);

      if (response.data.success) {
        setOrderData(response.data.orderData);
        setReportData(response.data.reportData);
        setHasValidationWarnings(response.data.hasWarnings);
        setValidationWarnings(response.data.validationWarnings || []);
        setShowApproveDialog(true);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch validation data';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      console.error('Validation fetch error:', error.response?.data);
    } finally {
      setLoadingValidation(false);
    }
  };

  const handleApprove = async (confirmMismatch = false, mismatchReason = '') => {
    console.log('========== FRONTEND: APPROVE BUTTON CLICKED ==========');
    console.log('Report ID:', id);
    console.log('confirmMismatch:', confirmMismatch);
    console.log('mismatchReason:', mismatchReason);
    console.log('approvalNotes:', approvalNotes);

    // Calculate cumulative review duration in seconds
    let reviewDuration = accumulatedTime;

    // If timer is currently active, add current session time
    if (isTimerActive && currentSessionStart !== null) {
      const now = Date.now();
      const currentSessionSeconds = Math.floor((now - currentSessionStart) / 1000);
      reviewDuration = accumulatedTime + currentSessionSeconds;
    }

    console.log('[REVIEW TIMER] Total review duration (seconds):', reviewDuration);
    console.log('[REVIEW TIMER] Accumulated time:', accumulatedTime);
    console.log('[REVIEW TIMER] Current session:', isTimerActive ? 'active' : 'paused');

    const payload = {
      comments: approvalNotes,
      confirmMismatch,
      mismatchReason,
      reviewDuration
    };

    console.log('Request payload:', JSON.stringify(payload, null, 2));
    console.log('Request URL:', `/review/${id}/approve`);

    try {
      console.log('Sending approval request...');
      const response = await api.post(`/review/${id}/approve`, payload);

      console.log('✓✓✓ APPROVAL SUCCESS! ✓✓✓');
      console.log('Response status:', response.status);
      console.log('Response data:', JSON.stringify(response.data, null, 2));

      // Remove review session from localStorage as review is complete
      if (id) {
        removeReviewSession(id);
        console.log('[REVIEW TIMER] Session removed from localStorage after approval');
      }

      enqueueSnackbar('Report approved successfully', { variant: 'success' });
      setShowApproveDialog(false);
      setShowValidationDialog(false);
      // Refresh the report to get updated status and finalData
      await fetchReport();
    } catch (error: any) {
      console.error('✗✗✗ APPROVAL FAILED ✗✗✗');
      console.error('Error object:', error);
      console.error('Error response status:', error.response?.status);
      console.error('Error response headers:', error.response?.headers);
      console.error('Error response data:', JSON.stringify(error.response?.data, null, 2));
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);

      if (error.response?.data) {
        console.error('Backend error details:');
        console.error('  - success:', error.response.data.success);
        console.error('  - message:', error.response.data.message);
        console.error('  - error:', error.response.data.error);
        console.error('  - requiresConfirmation:', error.response.data.requiresConfirmation);
        console.error('  - validationWarnings:', error.response.data.validationWarnings);
      }

      const errorMessage = error.response?.data?.message || 'Failed to approve report';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      console.error('========== END FRONTEND ERROR LOGGING ==========');
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      enqueueSnackbar('Please provide a rejection reason', { variant: 'warning' });
      return;
    }

    // Calculate cumulative review duration in seconds
    let reviewDuration = accumulatedTime;

    // If timer is currently active, add current session time
    if (isTimerActive && currentSessionStart !== null) {
      const now = Date.now();
      const currentSessionSeconds = Math.floor((now - currentSessionStart) / 1000);
      reviewDuration = accumulatedTime + currentSessionSeconds;
    }

    console.log('[REVIEW TIMER] Reject - Total review duration (seconds):', reviewDuration);
    console.log('[REVIEW TIMER] Accumulated time:', accumulatedTime);
    console.log('[REVIEW TIMER] Current session:', isTimerActive ? 'active' : 'paused');

    try {
      await api.post(`/review/${id}/reject`, {
        reason: rejectionReason,
        reviewDuration
      });

      // Remove review session from localStorage as review is complete
      if (id) {
        removeReviewSession(id);
        console.log('[REVIEW TIMER] Session removed from localStorage after rejection');
      }

      enqueueSnackbar('Report rejected', { variant: 'info' });
      navigate('/nurse/reports');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to reject report';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const handleReprocess = async () => {
    if (!id) return;

    try {
      setReprocessing(true);
      await api.post(`/reports/${id}/reprocess`);
      enqueueSnackbar('Report reprocessing started. Please wait and refresh the page.', { variant: 'info' });
      // Refresh the report data after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to reprocess report';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      setReprocessing(false);
    }
  };

  const handleSaveOrderId = async () => {
    if (!tempOrderId.trim()) {
      enqueueSnackbar('Please enter an Order ID', { variant: 'warning' });
      return;
    }

    setSavingOrderId(true);
    try {
      const response = await api.patch(`/review/${id}/orderId`, { orderId: tempOrderId.trim() });

      if (response.data.success) {
        setReport(prev => prev ? { ...prev, orderId: tempOrderId.trim() } : null);
        setEditingOrderId(false);
        enqueueSnackbar('Order ID saved successfully', { variant: 'success' });
        await fetchReport(); // Refresh to ensure we have the latest data
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to save Order ID';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSavingOrderId(false);
    }
  };

  // Preview the re-publish (dry-run) — shows every before->after without writing.
  const openRepublishDialog = async (choices: Record<string, string> = {}) => {
    setRepublishApplied(false);
    setRepublishPreview(null);
    setRepublishKeepChoices(choices);
    setShowRepublishDialog(true);
    setIsRepublishing(true);
    try {
      const res = await api.post(`/review/${id}/republish?dryRun=true`, { keepChoices: choices });
      setRepublishPreview(res.data);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || 'Re-publish preview failed', { variant: 'error' });
      setShowRepublishDialog(false);
    } finally {
      setIsRepublishing(false);
    }
  };

  // Operator picked which row (value) to keep for a conflicting duplicate -> re-run dry-run.
  const handleConflictChoice = async (key: string, keepId: string) => {
    const next = { ...republishKeepChoices, [key]: keepId };
    setRepublishKeepChoices(next);
    setIsRepublishing(true);
    try {
      const res = await api.post(`/review/${id}/republish?dryRun=true`, { keepChoices: next });
      setRepublishPreview(res.data);
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || 'Preview refresh failed', { variant: 'error' });
    } finally {
      setIsRepublishing(false);
    }
  };

  // Apply the re-publish — updates the stored record in place, then the cron can re-run.
  const handleRepublishApply = async () => {
    setIsRepublishing(true);
    try {
      const res = await api.post(`/review/${id}/republish?dryRun=false`, { keepChoices: republishKeepChoices });
      setRepublishPreview(res.data);
      setRepublishApplied(true);
      enqueueSnackbar(`Re-published: fixed ${res.data.totalChanges} field(s), removed ${res.data.duplicatesRemoved} duplicate row(s)`, { variant: 'success' });
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.message || 'Re-publish failed', { variant: 'error' });
    } finally {
      setIsRepublishing(false);
    }
  };

  // `choices` is passed explicitly by the value-confirmation dialog rather than read
  // from state, so a re-render can never publish with a stale set of choices.
  const handlePublish = async (
    confirmIncompleteMapping: boolean = false,
    choices?: Record<string, string>,
    confirmOrderConditions: boolean = false
  ) => {
    if (!id) return;

    const keepChoices = choices ?? valueChoices;
    // Record this call's acknowledgements, then send every one gathered so far.
    if (confirmIncompleteMapping) publishAcks.current.mapping = true;
    if (confirmOrderConditions) publishAcks.current.orderConditions = true;
    const acks = publishAcks.current;

    setIsPublishing(true);
    setShowPublishDialog(false);
    try {
      const response = await api.post(`/review/${id}/publish`, {
        ...(acks.mapping ? { confirmIncompleteMapping: true } : {}),
        ...(acks.orderConditions ? { confirmOrderConditions: true } : {}),
        ...(Object.keys(keepChoices).length ? { keepChoices } : {})
      });

      // Phase 9: handle soft-gate responses (success:true + requiresConfirmation).
      // Branch on confirmationType — more than one soft gate uses this shape, and
      // a missing branch renders the wrong dialog with empty contents.
      if (response.data?.success === true && response.data?.requiresConfirmation === true) {
        if (response.data?.confirmationType === 'orderConditions') {
          setOrderConditionWarnings(response.data.orderWarnings || []);
          setShowOrderConditionsDialog(true);
          return;  // wait for the nurse to acknowledge
        }
        setMappingGateData({
          unmappedParameters: response.data.unmappedParameters || [],
          unconfirmedSuggestions: response.data.unconfirmedSuggestions || []
        });
        setShowMappingGateDialog(true);
        return;  // do NOT set isPublished — wait for nurse to confirm or cancel
      }

      // The same test came back with more than one value. Nothing was sent to the
      // model — the nurse must confirm the correct one against the source PDF.
      if (response.data?.success === true && response.data?.requiresValueConfirmation === true) {
        setValueConflicts(response.data.valueConflicts || []);
        return;  // do NOT set isPublished — wait for the nurse
      }

      if (response.data?.success) {
        setIsPublished(true);
        enqueueSnackbar(`Published ${response.data.insertedCount} test results successfully`, { variant: 'success' });
        await fetchReport();
      }
    } catch (error: any) {
      // Read `error` as well as `message`: the publish gates historically replied
      // with an { ok, error } shape, and a swallowed reason shows the nurse a
      // generic failure she cannot act on.
      const d = error.response?.data;
      const errorMessage = d?.message || d?.error || 'Failed to publish report';
      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 12000,
        style: { whiteSpace: 'pre-line' },
      });
    } finally {
      setIsPublishing(false);
    }
  };

  // ===== Phase 11: Vendor mapping handlers =====

  // Update a single result's vendorMapping in local state (called by MappingStatusChip after a successful PUT)
  const handleMappingUpdated = (resultIndex: number, newMapping: VendorMapping, newSummary?: any) => {
    if (!report) return;
    const updatedResults = [...(report.extractedData?.results || [])];
    if (updatedResults[resultIndex]) {
      updatedResults[resultIndex] = { ...updatedResults[resultIndex], vendorMapping: newMapping } as any;
    }
    // Update ONLY the affected row + the header summary in place. We deliberately do NOT refetch
    // the whole report here — refetching replaces the report object, remounts the table, and
    // throws the nurse back to the top of a long report after every single map. The server already
    // returned the authoritative vendorMappingSummary, so we apply it locally and stay put.
    // Replacing the summary object re-renders the whole results panel, which can nudge the scroll
    // position, so we snapshot the scroll offset and restore it after the commit paints.
    const scrollY = window.scrollY;
    setReport({
      ...report,
      extractedData: { ...report.extractedData, results: updatedResults },
      ...(newSummary ? { vendorMappingSummary: newSummary } : {})
    } as any);
    requestAnimationFrame(() => {
      window.scrollTo(0, scrollY);
      requestAnimationFrame(() => window.scrollTo(0, scrollY));
    });
  };

  const handleBulkConfirmMappings = async () => {
    if (!id) return;
    setBulkConfirming(true);
    try {
      const response = await api.post(`/review/${id}/mapping/bulk-confirm`);
      if (response.data?.success) {
        enqueueSnackbar(`Confirmed ${response.data.confirmedCount} mappings`, { variant: 'success' });
        await fetchReport();
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to bulk confirm mappings', { variant: 'error' });
    } finally {
      setBulkConfirming(false);
    }
  };

  const handleRetriggerMapping = async () => {
    if (!id) return;
    setRetriggeringMapping(true);
    try {
      const response = await api.post(`/review/${id}/mapping/retrigger`);
      if (response.data?.success) {
        enqueueSnackbar('Mapping re-run successfully', { variant: 'success' });
        await fetchReport();
      }
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to re-run mapping', { variant: 'error' });
    } finally {
      setRetriggeringMapping(false);
    }
  };

  const handleDeleteClick = (parameterId: string, parameterName: string) => {
    setParameterToDelete({ id: parameterId, name: parameterName });
    setShowDeleteDialog(true);
  };

  const handleDeleteParameter = async () => {
    if (!parameterToDelete || !report) return;

    const closeDialog = () => {
      setShowDeleteDialog(false);
      setParameterToDelete(null);
    };

    // The exact rows to delete (sent as ids); refused when they cannot be told apart
    const shownRow = displayedRows(report.extractedData, currentPage).find(p => p._id === parameterToDelete.id);
    const deleteTarget = shownRow
      ? reviewRowRef(report.extractedData, shownRow, currentPage)
      : ({ ok: false, reason: 'not_found' } as const);
    if (!deleteTarget.ok) {
      enqueueSnackbar(rowTargetMessage(deleteTarget.reason), { variant: 'error' });
      closeDialog();
      return;
    }

    setDeleting(true);
    try {
      await api.delete(`/review/${id}/parameter/${deleteTarget.resultRowId}`, { params: deleteTarget.ids });
      enqueueSnackbar('Parameter deleted successfully', { variant: 'success' });
      closeDialog();
      // Refresh the report to get updated data
      await fetchReport();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete parameter';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      // The server refused (409: that row cannot be told apart, or is gone): nothing to retry here
      if (error.response?.status === 409) closeDialog();
    } finally {
      setDeleting(false);
    }
  };

  const handleRevalidate = async () => {
    if (!id) return;

    setRevalidating(true);
    try {
      const response = await api.post(`/reports/${id}/revalidate`);

      if (response.data.success) {
        setReport(response.data.data);
        const flagCount = response.data.data.validationFlags?.length || 0;
        enqueueSnackbar(
          `Parameters re-validated successfully. ${flagCount} validation flag(s) found.`,
          { variant: 'success' }
        );
        console.log('[REVALIDATE] Validation summary:', response.data.validationSummary);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to re-validate parameters';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setRevalidating(false);
    }
  };

  const handleAddToParameterMaster = (parameter: TestResult, validationFlag: any) => {
    setSelectedParameter(parameter);
    setSelectedValidationFlag(validationFlag);
    // Reset dialog state
    setParameterAction('create');
    setTargetParameter(null);
    setNewParameterId('');
    setValueType('numeric');
    setParameterDescription('');
    setShowParameterMasterDialog(true);
  };

  const handleAddToExclusion = (parameter: TestResult, validationFlag: any) => {
    setSelectedParameter(parameter);
    setSelectedValidationFlag(validationFlag);
    setShowExclusionDialog(true);
  };

  const handleParameterMasterSubmit = async () => {
    if (!selectedParameter) return;

    setSubmittingSuggestion(true);
    try {
      const suggestionData: any = {
        action: parameterAction,
        suggestedParameter: selectedParameter.serviceItemName,
        suggestedUnit: selectedParameter.unit,
        validationFlag: selectedValidationFlag,
        reportId: report?._id
      };

      if (parameterAction === 'create') {
        // For creating new parameter
        if (!newParameterId.trim()) {
          enqueueSnackbar('Parameter ID is required', { variant: 'warning' });
          setSubmittingSuggestion(false);
          return;
        }

        suggestionData.newParameterData = {
          parameterId: newParameterId.trim().toUpperCase().replace(/\s+/g, '_'),
          parameterName: selectedParameter.serviceItemName,
          valueType: valueType,
          possibleUnits: selectedParameter.unit ? [selectedParameter.unit] : [],
          description: parameterDescription.trim()
        };
      } else if (parameterAction === 'update_alias') {
        // For adding as alias to existing parameter
        if (!targetParameter) {
          enqueueSnackbar('Please select a target parameter', { variant: 'warning' });
          setSubmittingSuggestion(false);
          return;
        }

        suggestionData.targetParameterId = targetParameter.parameterId;
        suggestionData.targetParameterName = targetParameter.parameterName;

        // If it's a unit mismatch, change action to update_unit
        if (selectedValidationFlag?.flagType === 'UNIT_MISMATCH' && selectedParameter.unit) {
          suggestionData.action = 'update_unit';
        }
      }

      const response = await api.post('/parameter-suggestions', suggestionData);

      if (response.data.success) {
        enqueueSnackbar('Parameter suggestion submitted for approval', { variant: 'success' });
        setShowParameterMasterDialog(false);
        // Optionally refresh report
        await fetchReport();
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to submit parameter suggestion';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSubmittingSuggestion(false);
    }
  };

  const handleExclusionSubmit = async (data: any) => {
    try {
      if (isAdmin) {
        // Admin can directly add to exclusion list
        const response = await api.post('/exclusions', {
          excludedParameter: data.parameterName,
          unit: data.unit || null,
          labName: report?.extractedData?.labName || null,
          reason: data.reason,
        });

        if (response.data.success) {
          enqueueSnackbar('Parameter excluded successfully', { variant: 'success' });
          setShowExclusionDialog(false);
          setMismatchReason('');
        }
      } else {
        // Non-admin creates a suggestion for approval
        const response = await api.post('/exclusion-suggestions', {
          suggestedParameter: data.parameterName,
          suggestedUnit: data.unit || null,
          suggestedLabName: report?.extractedData?.labName || null,
          reason: data.reason,
          reportId: id
        });

        if (response.data.success) {
          enqueueSnackbar('Exclusion suggestion submitted for admin approval', { variant: 'success' });
          setShowExclusionDialog(false);
          setMismatchReason('');
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to process exclusion';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  const getValueIndicator = (parameter: TestParameter | TestResult) => {
    // Try referenceRange first, fall back to normalRange
    const range = parameter.referenceRange || parameter.normalRange;
    if (!range) return null;

    // "6,100" is 6100 (parseFloat read 6); an ambiguous value ("5,5") gets no indicator rather than a wrong one
    const value = readNumber(parameter.value).number;

    // Handle both referenceRange format {low, high} and normalRange format {min, max}
    const min = 'low' in range ? range.low : 'min' in range ? range.min : null;
    const max = 'high' in range ? range.high : 'max' in range ? range.max : null;

    if (value === null || min === null || max === null) return null;

    if (value < min) {
      const deviation = ((min - value) / min) * 100;
      return {
        icon: <TrendingDown />,
        color: deviation > 20 ? theme.palette.error.main : theme.palette.warning.main,
        label: 'Low',
        severity: deviation > 20 ? 'critical' : 'warning'
      };
    } else if (value > max) {
      const deviation = ((value - max) / max) * 100;
      return {
        icon: <TrendingUp />,
        color: deviation > 20 ? theme.palette.error.main : theme.palette.warning.main,
        label: 'High',
        severity: deviation > 20 ? 'critical' : 'warning'
      };
    }

    return {
      icon: <NormalIcon />,
      color: theme.palette.success.main,
      label: 'Normal',
      severity: 'normal'
    };
  };

  const getFlagColor = () => {
    switch (resultLabel(report).color) {
      case 'green':
        return theme.palette.success.main;
      case 'yellow':
        return theme.palette.warning.light;
      case 'orange':
        return theme.palette.warning.main;
      case 'red':
        return theme.palette.error.main;
      default:
        return theme.palette.grey[400];
    }
  };

  const getReferenceRangeDisplay = (parameter: TestParameter | TestResult): string => {
    if (parameter.referenceRange) {
      // Check if it has the string referenceRange property
      if ('referenceRange' in parameter.referenceRange && typeof parameter.referenceRange.referenceRange === 'string') {
        // Return '-' if the string is "null", otherwise return the actual value
        return parameter.referenceRange.referenceRange !== 'null' ? parameter.referenceRange.referenceRange : '-';
      }
      // Check if it has low/high numeric values
      if (parameter.referenceRange.low !== null && parameter.referenceRange.high !== null) {
        return `${parameter.referenceRange.low} - ${parameter.referenceRange.high}`;
      }
    }
    // Fall back to normalRange
    if (parameter.normalRange) {
      return `${parameter.normalRange.min} - ${parameter.normalRange.max}`;
    }
    return '-';
  };

  // Get column order from report or use default
  const columnOrder = report?.extractedData?.columnOrder || ['Parameter', 'Value', 'Normal Range', 'Unit'];
  console.log('[REVIEW] Column order:', columnOrder);

  // Helper function to find validation flag for a specific parameter and field
  const findValidationFlag = (parameterName: string, fieldName: string) => {
    return report?.validationFlags?.find(
      (flag) => flag.parameterName === parameterName && flag.field === fieldName
    ) || null;
  };

  // Duplicate-value conflicts are flagged on field 'value', but the read-only Value cell
  // does not wrap in <ValidationFlag> (only the edit-mode input does), so they would be
  // invisible until the nurse opened the row. Look them up by flagType explicitly — a
  // plain findValidationFlag(name, 'value') can be shadowed by a VALUE_TYPE_MISMATCH on
  // the same parameter, since master-validation flags are prepended to the array.
  const findDuplicateConflictFlag = (parameterName: string) => {
    return report?.validationFlags?.find(
      (flag) => flag.parameterName === parameterName && flag.flagType === 'DUPLICATE_VALUE_CONFLICT'
    ) || null;
  };

  // Helper function to render cell content based on column name
  const renderCellContent = (columnName: string, parameter: TestResult, isEditing: boolean, isAbnormal: boolean, indicator: any) => {
    switch (columnName) {
      case 'Parameter':
        const parameterFlag = findValidationFlag(parameter.serviceItemName, 'parameterName');
        return parameterFlag ? (
          <ValidationFlag flag={parameterFlag}>
            <Typography variant="body2" sx={{ fontWeight: isAbnormal ? 600 : 400, color: '#111827', fontSize: '14px', px: 1 }}>
              {parameter.serviceItemName}
            </Typography>
          </ValidationFlag>
        ) : (
          <Typography variant="body2" sx={{ fontWeight: isAbnormal ? 600 : 400, color: '#111827', fontSize: '14px' }}>
            {parameter.serviceItemName}
          </Typography>
        );

      case 'Value':
        return isEditing ? (
          <ValidationFlag flag={findValidationFlag(parameter.serviceItemName, 'value')}>
            <TextField
              size="small"
              value={editedValues[`${parameter._id}_value`] || parameter.value}
              onChange={(e) => setEditedValues(prev => ({
                ...prev,
                [`${parameter._id}_value`]: e.target.value
              }))}
              sx={{ width: 100 }}
            />
          </ValidationFlag>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: isAbnormal ? 600 : 400, color: '#111827', fontSize: '14px' }}>
              {parameter.value && parameter.value !== 'null' ? parameter.value : ''}
            </Typography>
            {(() => {
              const duplicateFlag = findDuplicateConflictFlag(parameter.serviceItemName);
              if (!duplicateFlag) return null;
              return (
                <Tooltip title={duplicateFlag.message || 'This parameter appears more than once with different values. You pick the correct one at publish.'} arrow placement="top">
                  <Chip
                    label="duplicate"
                    size="small"
                    color="warning"
                    variant="outlined"
                    icon={<WarningIcon sx={{ fontSize: 14 }} />}
                    sx={{ height: 20, fontSize: '11px', fontWeight: 600 }}
                  />
                </Tooltip>
              );
            })()}
            {indicator && indicator.severity !== 'normal' && (
              <Chip
                label={indicator.label}
                icon={indicator.icon}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '11px',
                  fontWeight: 600,
                  bgcolor: indicator.color + '15',
                  color: indicator.color,
                  border: `1px solid ${indicator.color}40`,
                  '& .MuiChip-icon': {
                    fontSize: '14px',
                    color: indicator.color,
                    marginLeft: '4px'
                  }
                }}
              />
            )}
          </Box>
        );

      case 'Unit':
        return isEditing ? (
          <ValidationFlag flag={findValidationFlag(parameter.serviceItemName, 'unit')}>
            <TextField
              size="small"
              value={editedValues[`${parameter._id}_unit`] || parameter.unit}
              onChange={(e) => setEditedValues(prev => ({
                ...prev,
                [`${parameter._id}_unit`]: e.target.value
              }))}
              sx={{ width: 80 }}
            />
          </ValidationFlag>
        ) : (
          <Typography variant="body2" sx={{ fontWeight: isAbnormal ? 500 : 400, color: '#6B7280', fontSize: '14px' }}>
            {parameter.unit && parameter.unit !== 'null' ? parameter.unit : ''}
          </Typography>
        );

      case 'Normal Range':
        return isEditing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              <TextField
                size="small"
                label="Low"
                value={editedValues[`${parameter._id}_refLow`] ?? ''}
                onChange={(e) => setEditedValues(prev => ({
                  ...prev,
                  [`${parameter._id}_refLow`]: e.target.value
                }))}
                sx={{ width: 70 }}
              />
              <Typography variant="body2">-</Typography>
              <TextField
                size="small"
                label="High"
                value={editedValues[`${parameter._id}_refHigh`] ?? ''}
                onChange={(e) => setEditedValues(prev => ({
                  ...prev,
                  [`${parameter._id}_refHigh`]: e.target.value
                }))}
                sx={{ width: 70 }}
              />
            </Box>
            <TextField
              size="small"
              label="Range Text"
              value={editedValues[`${parameter._id}_refRange`] ?? ''}
              onChange={(e) => setEditedValues(prev => ({
                ...prev,
                [`${parameter._id}_refRange`]: e.target.value
              }))}
              fullWidth
            />
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '14px' }}>
            {getReferenceRangeDisplay(parameter)}
          </Typography>
        );

      default:
        return null;
    }
  };

  // Get page-wise data if available, otherwise fall back to all results
  const pageWiseData = report?.extractedData?.pageWiseData;
  const hasPageWiseData = pageWiseData && pageWiseData.length > 0;
  const totalPages = hasPageWiseData ? pageWiseData.length : 1;

  // Page classification (2026-08). Old reports have no pageType at all, so key every
  // check on pageType === 'non_results' — never on droppedRowCount, which the schema
  // defaults to 0 on results pages too.
  const currentPageMeta = hasPageWiseData
    ? pageWiseData.find((p) => p.pageNumber === currentPage)
    : undefined;
  const skippedPages = hasPageWiseData
    ? pageWiseData.filter((p) => p.pageType === 'non_results')
    : [];

  // Get results for current page
  const currentPageResults = displayedRows(report?.extractedData, currentPage);

  // Separate excluded and active parameters
  const excludedParams: TestResult[] = [];
  const activeParams: TestResult[] = [];

  currentPageResults.forEach((param: TestResult) => {
    const hasExclusionFlag = report?.validationFlags?.some(
      (flag: any) =>
        flag.parameterName === param.serviceItemName &&
        flag.isExcluded === true
    );

    if (hasExclusionFlag) {
      excludedParams.push(param);
    } else {
      activeParams.push(param);
    }
  });

  // Filter active parameters based on search term (excluded params don't appear in search)
  const filteredResults = activeParams.filter((param: TestResult) =>
    param.serviceItemName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    param.value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Page change handler
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    console.log('[REVIEW] Page changed to:', newPage);
  };

  console.log('[REVIEW] 18. Render check - loading:', loading, 'report:', !!report);
  console.log('[REVIEW] 18a. Page-wise data available:', hasPageWiseData);
  console.log('[REVIEW] 18b. Total pages:', totalPages);
  console.log('[REVIEW] 18c. Current page:', currentPage);
  console.log('[REVIEW] 18d. Current page results:', currentPageResults.length);

  if (loading) {
    console.log('[REVIEW] 19. Showing loading spinner');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!report) {
    console.error('[REVIEW] ERROR: No report data to display');
    return (
      <Alert severity="error">Report not found</Alert>
    );
  }

  console.log('[REVIEW] 20. Rendering report UI for:', report.orderId);
  console.log('[REVIEW] 21. Report status:', report.status);
  console.log('[REVIEW] 22. Has extractedData:', !!report.extractedData);
  console.log('[REVIEW] 23. Results count:', report.extractedData?.results?.length || 0);

  // Show success message with View JSON button if report is approved/published and not in repeat review mode
  if ((report.status === 'approved' || report.status === 'published') && !showRepeatReview && report.finalData) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: appTheme.colors.background,
        fontFamily: appTheme.typography.fontFamily,
        padding: `${appTheme.spacing.xl} ${appTheme.spacing['2xl']}`,
      }}>
        {/* Success Message Card */}
        <Card sx={{ maxWidth: 800, mx: 'auto', mb: 3 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              {report.status === 'published' ? 'Report Published Successfully' : 'Report Approved Successfully'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {report.status === 'published'
                ? 'The report has been published. No further changes can be made.'
                : 'The report has been approved and is ready for integration.'}
            </Typography>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<CodeIcon />}
                onClick={() => setShowJsonOutput(!showJsonOutput)}
              >
                {showJsonOutput ? 'Hide JSON' : 'View JSON'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/nurse/reports')}
              >
                Back to Reports
              </Button>
              {report.status === 'published' && (
                <Button
                  variant="outlined"
                  color="info"
                  onClick={() => setShowRepeatReview(true)}
                >
                  Review (Read-Only)
                </Button>
              )}
              {report.status === 'published' && (
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<PublishIcon />}
                  onClick={() => openRepublishDialog()}
                >
                  Fix &amp; Re-send to Model
                </Button>
              )}
              {report.status !== 'published' && (
                <Button
                  variant="outlined"
                  onClick={() => setShowRepeatReview(true)}
                >
                  Repeat Review
                </Button>
              )}
              {report.status !== 'published' && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={isPublishing ? <CircularProgress size={20} color="inherit" /> : <PublishIcon />}
                  onClick={() => setShowPublishDialog(true)}
                  disabled={isPublishing || isPublished}
                >
                  {isPublishing ? 'Publishing...' : 'Publish'}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Conditionally Rendered JSON Output */}
        {showJsonOutput && (
          <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            <JsonOutputView
              jsonData={report.finalData}
              orderId={report.orderId}
              onRepeatReview={() => setShowRepeatReview(true)}
              onBack={() => navigate('/nurse/reports')}
            />
          </Box>
        )}

        {/* Publish Confirmation Dialog - needed in success view */}
        <Dialog open={showPublishDialog} onClose={() => setShowPublishDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PublishIcon sx={{ color: '#10B981' }} />
            Confirm Publish
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Are you sure you want to publish this data?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Once published, you won't be able to update this report.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setShowPublishDialog(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => { publishAcks.current = { mapping: false, orderConditions: false }; handlePublish(false); }}
              startIcon={<PublishIcon />}
            >
              Publish
            </Button>
          </DialogActions>
        </Dialog>

        {/* Order-condition warning. status / part_of_package no longer block
            publishing — outsourced-lab orders never reach "Final" — but the
            nurse is told what looks unusual before the data leaves the tool. */}
        <Dialog open={showOrderConditionsDialog} onClose={() => setShowOrderConditionsDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#B45309' }}>
            <WarningIcon /> Check before publishing
          </DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              This report can be published, but the HIMS order looks unusual:
            </Typography>
            <ul style={{ marginTop: 4, paddingLeft: 20 }}>
              {orderConditionWarnings.map((w, i) => (
                <li key={i} style={{ fontSize: 13, marginBottom: 4 }}>{w}</li>
              ))}
            </ul>
            <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
              The test results themselves are unaffected and will be sent correctly.
              Publish anyway, or cancel if this looks like the wrong order.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setShowOrderConditionsDialog(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() => { setShowOrderConditionsDialog(false); handlePublish(false, undefined, true); }}
              startIcon={<PublishIcon />}
            >
              Publish Anyway
            </Button>
          </DialogActions>
        </Dialog>

        {/* Re-publish (Fix & Re-send) Dialog — dry-run preview then apply */}
        <Dialog open={showRepublishDialog} onClose={() => setShowRepublishDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PublishIcon sx={{ color: '#F59E0B' }} />
            Fix &amp; Re-send to Model {republishApplied ? '— Applied' : '(preview)'}
          </DialogTitle>
          <DialogContent dividers>
            {isRepublishing && !republishPreview ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                <CircularProgress size={20} /> <Typography>Checking the stored record…</Typography>
              </Box>
            ) : republishPreview ? (
              <>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Vendor <b>{republishPreview.vendorName}</b> · gender <b>{String(republishPreview.gender)}</b> ·{' '}
                  <b>{republishPreview.totalChanges}</b> field(s) to fill across <b>{republishPreview.rowsChanged}</b> of {republishPreview.rowCount} rows
                  {republishPreview.namesReresolved ? <> · <b style={{ color: '#1F6FB2' }}>{republishPreview.namesReresolved} name(s) corrected to master</b></> : null}
                  {republishPreview.duplicatesToRemove ? <> · <b style={{ color: '#B03A2E' }}>{republishPreview.duplicatesToRemove} duplicate row(s) to remove</b></> : null}
                  {republishPreview.conflictsUnresolved ? <> · <b style={{ color: '#B03A2E' }}>{republishPreview.conflictsUnresolved} conflict(s) need a choice</b></> : null}.
                  {republishPreview.rowsWithoutReference ? ` (${republishPreview.rowsWithoutReference} rows have no master reference.)` : ''}
                </Typography>
                {(republishPreview.duplicates || []).length > 0 && (
                  <Box sx={{ mb: 1, p: 1, border: '1px solid #f0d0cc', borderRadius: 1, backgroundColor: '#FDF6F5' }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Duplicate rows removed (safe)</Typography>
                    {(republishPreview.duplicates || []).map((d: any, i: number) => (
                      <Typography key={i} variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                        – {d.serviceItemName} — {d.reason}
                      </Typography>
                    ))}
                  </Box>
                )}
                {(republishPreview.conflicts || []).length > 0 && !republishApplied && (
                  <Box sx={{ mb: 1, p: 1.25, border: '1px solid #f0c8c4', borderRadius: 1, backgroundColor: '#FDF3F2' }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5, color: '#B03A2E' }}>
                      Conflicting values — check the report and pick the correct one (the tool will NOT choose):
                    </Typography>
                    {(republishPreview.conflicts || []).map((c: any, i: number) => (
                      <Box key={i} sx={{ mb: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>{c.name}</Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.25 }}>
                          {c.options.map((o: any) => (
                            <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                              <input
                                type="radio"
                                name={`conflict-${c.key}`}
                                checked={republishKeepChoices[c.key] === o.id}
                                disabled={isRepublishing}
                                onChange={() => handleConflictChoice(c.key, o.id)}
                              />
                              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>keep {o.value}</Typography>
                            </label>
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
                {republishApplied && (
                  <Typography variant="body2" color="success.main" sx={{ mb: 1 }}>
                    ✓ Applied to the record on prod. You can now trigger the cron.
                  </Typography>
                )}
                <Box sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
                  {(republishPreview.details || []).filter((d: any) => d.changes && d.changes.length).map((d: any, i: number) => (
                    <Box key={i} sx={{ p: 1, borderBottom: '1px solid #f2f2f2' }}>
                      <Typography variant="subtitle2">{d.serviceItemName}</Typography>
                      {d.changes.map((c: any, j: number) => (
                        <Typography key={j} variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                          {c.field}: "{String(c.from)}" → "{String(c.to)}"
                        </Typography>
                      ))}
                    </Box>
                  ))}
                  {republishPreview.totalChanges === 0 && !republishPreview.duplicatesToRemove && !republishPreview.conflictsUnresolved && (
                    <Box sx={{ p: 2 }}><Typography variant="body2">Nothing to fix — the record already matches the master.</Typography></Box>
                  )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Blank reference fields (unit / method / range) are filled from the master; values already present are never changed. Blank duplicate rows are removed. The patient value is never touched.
                </Typography>
              </>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setShowRepublishDialog(false)} variant="outlined">Close</Button>
            <Button
              variant="contained"
              color="warning"
              disabled={isRepublishing || republishApplied || !republishPreview
                || republishPreview.conflictsUnresolved > 0
                || (republishPreview.totalChanges === 0 && !republishPreview.duplicatesToRemove)}
              startIcon={isRepublishing ? <CircularProgress size={18} color="inherit" /> : <PublishIcon />}
              onClick={handleRepublishApply}
            >
              {isRepublishing ? 'Applying…'
                : republishPreview && republishPreview.conflictsUnresolved > 0 ? 'Resolve conflicts to continue'
                : 'Apply & Re-send'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Value-confirmation gate: same test, more than one value. HARD gate — there is
            no onClose, so it cannot be dismissed by clicking away, and "Confirm" stays
            disabled until every conflict has been answered. The tool never picks. */}
        <Dialog open={!!valueConflicts && valueConflicts.length > 0} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#92400E' }}>
            <WarningIcon sx={{ color: '#F59E0B' }} />
            Confirm the correct value
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              These tests were read with more than one value. Open the source PDF and pick the
              correct one. Nothing is sent to the model until every test below is confirmed.
            </Alert>
            {(valueConflicts || []).map((c) => (
              <Box key={c.key} sx={{ mb: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{c.name}</Typography>
                <RadioGroup
                  value={valueChoices[c.key] ?? ''}
                  onChange={(e) => setValueChoices({ ...valueChoices, [c.key]: e.target.value })}
                >
                  {c.options.map((o) => (
                    <FormControlLabel
                      key={o.id}
                      value={o.id}
                      control={<Radio />}
                      label={
                        <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 14 }}>
                          {o.value}{o.unit ? ` ${o.unit}` : ''}
                          {o.referenceRange ? `  (ref ${o.referenceRange})` : ''}
                          {o.method ? `  · ${o.method}` : ''}
                        </Typography>
                      }
                    />
                  ))}
                </RadioGroup>
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setValueConflicts(null); setValueChoices({}); }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={(valueConflicts || []).some((c) => !valueChoices[c.key])}
              onClick={() => {
                const confirmed = valueChoices;
                setValueConflicts(null);
                handlePublish(false, confirmed);
              }}
            >
              Confirm and send to model
            </Button>
          </DialogActions>
        </Dialog>

        {/* Phase 9: Vendor Mapping Gate Dialog (soft gate) */}
        <Dialog open={showMappingGateDialog} onClose={() => setShowMappingGateDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#92400E' }}>
            <PublishIcon sx={{ color: '#F59E0B' }} />
            Incomplete Vendor Mapping
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Some parameters don't have a confirmed vendor mapping. They will be published with their original extracted names.
            </Typography>
            {mappingGateData.unconfirmedSuggestions.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#92400E' }}>
                  {mappingGateData.unconfirmedSuggestions.length} unconfirmed suggestion(s):
                </Typography>
                <ul style={{ marginTop: 4, paddingLeft: 20 }}>
                  {mappingGateData.unconfirmedSuggestions.slice(0, 10).map((s, i) => (
                    <li key={i} style={{ fontSize: 13 }}>
                      <strong>{s.serviceItemName}</strong> → suggested: <em>{s.suggestedName}</em> ({s.confidence}%)
                    </li>
                  ))}
                  {mappingGateData.unconfirmedSuggestions.length > 10 && (
                    <li style={{ fontSize: 13, opacity: 0.7 }}>...and {mappingGateData.unconfirmedSuggestions.length - 10} more</li>
                  )}
                </ul>
              </Box>
            )}
            {mappingGateData.unmappedParameters.length > 0 && (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#991B1B' }}>
                  {mappingGateData.unmappedParameters.length} unmapped parameter(s):
                </Typography>
                <ul style={{ marginTop: 4, paddingLeft: 20 }}>
                  {mappingGateData.unmappedParameters.slice(0, 10).map((p, i) => (
                    <li key={i} style={{ fontSize: 13 }}>{p.serviceItemName}</li>
                  ))}
                  {mappingGateData.unmappedParameters.length > 10 && (
                    <li style={{ fontSize: 13, opacity: 0.7 }}>...and {mappingGateData.unmappedParameters.length - 10} more</li>
                  )}
                </ul>
              </Box>
            )}
            <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
              Click "Publish Anyway" to publish with the original names, or "Cancel" to go back and confirm/correct mappings.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setShowMappingGateDialog(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="warning"
              onClick={() => { setShowMappingGateDialog(false); handlePublish(true); }}
              startIcon={<PublishIcon />}
            >
              Publish Anyway
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }

  // Otherwise show the split-view for review/editing
  return (
    <div style={{
      fontFamily: appTheme.typography.fontFamily,
    }}>
      {/* Header */}
      <div style={{ marginBottom: appTheme.spacing.lg }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: appTheme.spacing.md }}>
          <div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: 700,
              color: appTheme.colors.textPrimary,
              margin: 0,
              marginBottom: appTheme.spacing.xs,
            }}>
              Review Lab Report
            </h1>
            <p style={{
              fontSize: appTheme.typography.sizes.body,
              color: appTheme.colors.textSecondary,
              margin: 0,
            }}>
              Review and approve the extracted data from the lab report
            </p>
          </div>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Tooltip title="View Edit History">
              <IconButton onClick={() => setShowHistory(true)} color="primary" size="small">
                <Badge badgeContent={report.editHistory?.length || 0} color="error">
                  <HistoryIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>
            <Chip
              label={report.status.toUpperCase()}
              size="small"
              sx={{
                height: 20,
                fontSize: '10px',
                fontWeight: 600,
                bgcolor: report.status === 'ready' ? '#FEF3C7' : report.status === 'approved' ? '#D1FAE5' : report.status === 'published' ? '#DBEAFE' : report.status === 'rejected' ? '#FEE2E2' : '#F3F4F6',
                color: report.status === 'ready' ? '#B45309' : report.status === 'approved' ? '#065F46' : report.status === 'published' ? '#1D4ED8' : report.status === 'rejected' ? '#991B1B' : '#6B7280',
                border: `1px solid ${report.status === 'ready' ? '#FCD34D' : report.status === 'approved' ? '#6EE7B7' : report.status === 'published' ? '#93C5FD' : report.status === 'rejected' ? '#FECACA' : '#D1D5DB'}`
              }}
            />
          </Box>
        </div>

        {/* Bypass-source card — only rendered when the report was created via the Bypass CSV ingest flow */}
        {(report as any)?.bypassInfo?.source === 'csv' && (
          <Alert severity="warning" sx={{ mb: 2 }} icon={<HistoryIcon fontSize="inherit" />}>
            <strong>Source: Bypass CSV.</strong> This report was created manually via the Bypass tab
            {(report as any).bypassInfo.csvFileName ? <> from <code>{(report as any).bypassInfo.csvFileName}</code></> : null}
            {(report as any).bypassInfo.csvRowCount ? <> ({(report as any).bypassInfo.csvRowCount} parameter rows)</> : null}
            {(report as any).bypassInfo.overrodePreviousStatus ? <>. It replaced a previous report with status <strong>{(report as any).bypassInfo.overrodePreviousStatus}</strong></> : null}.
          </Alert>
        )}

        {/* Repeat Review Alert */}
        {showRepeatReview && (
          <Alert
            severity={report.status === 'published' ? 'warning' : 'info'}
            icon={<InfoIcon />}
            sx={{ mb: 2 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {report.status === 'published' ? 'Review Mode (Read-Only)' : 'Repeat Review Mode'}
            </Typography>
            <Typography variant="body2">
              {report.status === 'published'
                ? 'You are viewing a published report. No changes can be made.'
                : 'You are re-reviewing an approved report. Make your changes and click "Re-Approve" to regenerate the JSON output.'}
            </Typography>
          </Alert>
        )}

        {/* Status Alert */}
        {report.flags?.requiresAttention && (
          <Alert
            severity="warning"
            icon={<WarningIcon />}
            sx={{ mb: 2 }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Attention Required
            </Typography>
            <Typography variant="body2">
              {report.flags.summary}
            </Typography>
          </Alert>
        )}

        {/* Incomplete Processing Alert */}
        {report.processingIssues?.hasIncompleteProcessing && (
          <Alert
            severity="error"
            icon={<WarningIcon />}
            sx={{ mb: 2 }}
            action={
              // Same rule as the backend (utils/permissions.ts); admins are unchanged (the flag always passes for them)
              canReprocessReport(user, report) ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleReprocess}
                  disabled={reprocessing}
                  startIcon={reprocessing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                >
                  {reprocessing ? 'Reprocessing...' : 'Reprocess'}
                </Button>
              ) : undefined
            }
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Incomplete Processing Detected
            </Typography>
            <Typography variant="body2">
              {report.processingIssues.message || `Only ${report.processingIssues.processedPages} of ${report.processingIssues.totalPages} pages were processed successfully.`}
              {report.processingIssues.failedPages?.length > 0 && (
                <> Failed pages: {report.processingIssues.failedPages.join(', ')}</>
              )}
            </Typography>
          </Alert>
        )}

        {/* Summary-only extraction warning — this PDF had no lab result tables at all */}
        {report.extractedData?.extractionSource === 'summary_only' && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Extracted from summary pages
            </Typography>
            <Typography variant="body2">
              This PDF contains no lab result tables — values were read from the report's
              summary/dashboard pages. Units and reference ranges may be incomplete (the vendor
              master fills what it can). Ask for the full lab report if possible.
            </Typography>
          </Alert>
        )}
      </div>

      {/* Split View: PDF Viewer + Extracted Data */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Left Side: PDF Viewer (or CSV panel for bypass-sourced reports) */}
        <Grid size={{ xs: 12, lg: 5 }}>
          <Box sx={{ position: 'sticky', top: 16, height: 'calc(100vh - 150px)' }}>
            {(() => {
              const bypassReport = (report as any)?.bypassInfo?.source === 'csv';
              if (bypassReport) {
                const bi = (report as any).bypassInfo || {};
                return (
                  <BypassCsvPanel
                    csvFileName={bi.csvFileName}
                    csvRowCount={bi.csvRowCount}
                    bypassedAt={bi.bypassedAt}
                    results={report.extractedData?.results || []}
                  />
                );
              }
              // PDF is served through a short-lived, report-scoped link (never the old public uploads folder)
              return (
                <ReportPdfViewer
                  reportId={report._id}
                  currentPage={currentPage}
                  totalPages={hasPageWiseData ? totalPages : undefined}
                  onPageChange={hasPageWiseData ? handlePageChange : undefined}
                />
              );
            })()}
          </Box>
        </Grid>

        {/* Right Side: Extracted Data */}
        <Grid size={{ xs: 12, lg: 7 }}>
          {/* Report Info Cards */}
          <Grid container spacing={1.5} sx={{ mb: 2, alignItems: 'stretch' }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Tooltip
            title={report.orderId ? `Click to edit` : 'Required before approval'}
            placement="bottom"
            arrow
          >
          <Card sx={{
            bgcolor: !report.orderId ? '#FEF3C7' : 'white',
            border: !report.orderId ? '1px solid #F59E0B' : '1px solid #E5E7EB',
            borderRadius: 1.5,
            boxShadow: 'none',
            height: '100%',
            minHeight: 48,
            cursor: (report.orderId && report.status !== 'published') ? 'pointer' : 'default',
            '&:hover': (report.orderId && report.status !== 'published') ? { bgcolor: '#EEF2FF' } : {}
          }}
          onClick={() => {
            if (report.orderId && !editingOrderId && report.status !== 'published') {
              setTempOrderId(report.orderId || '');
              setEditingOrderId(true);
            }
          }}
          >
            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 500, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, display: 'flex', alignItems: 'center' }}>
                <ReportIcon sx={{ mr: 0.5, color: !report.orderId ? '#F59E0B' : '#4361EE', fontSize: 14 }} />
                Order ID {!report.orderId && <span style={{ color: '#EF4444', marginLeft: 2 }}>*</span>}
              </Typography>
              {!report.orderId || editingOrderId ? (
                <Box onClick={(e) => e.stopPropagation()}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Enter Order ID"
                    value={editingOrderId ? tempOrderId : ''}
                    onChange={(e) => setTempOrderId(e.target.value)}
                    onFocus={() => setEditingOrderId(true)}
                    autoFocus={!report.orderId}
                    disabled={savingOrderId}
                    InputProps={{ sx: { fontSize: '12px', height: 28 } }}
                  />
                  <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75 }}>
                    <Button size="small" variant="contained" onClick={handleSaveOrderId} disabled={savingOrderId || !tempOrderId.trim()} sx={{ fontSize: '11px', py: 0.25, px: 1.5, textTransform: 'none' }}>
                      {savingOrderId ? 'Saving...' : 'Save'}
                    </Button>
                    {report.orderId && (
                      <Button size="small" variant="outlined" onClick={() => { setEditingOrderId(false); setTempOrderId(''); }} disabled={savingOrderId} sx={{ fontSize: '11px', py: 0.25, px: 1, textTransform: 'none' }}>
                        Cancel
                      </Button>
                    )}
                  </Box>
                </Box>
              ) : (
                <Typography sx={{ fontWeight: 500, color: '#111827', wordBreak: 'break-all', fontSize: '13px', lineHeight: 1.3 }}>
                  {report.orderId}
                </Typography>
              )}
            </CardContent>
          </Card>
          </Tooltip>
        </Grid>

        <Grid size={{ xs: 6, md: 2.5 }}>
          <Tooltip title={report.extractedData?.labAddress || 'No address'} placement="bottom" arrow>
          <Card sx={{
            bgcolor: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: 1.5,
            boxShadow: 'none',
            height: '100%',
            minHeight: 48
          }}>
            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 500, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, display: 'flex', alignItems: 'center' }}>
                <LabIcon sx={{ mr: 0.5, color: '#8B5CF6', fontSize: 14 }} />
                Laboratory
              </Typography>
              {report.status === 'published' || !editingLabName ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontWeight: 500, color: '#111827', fontSize: '13px', lineHeight: 1.3 }}>
                    {report.extractedData?.labName || 'Unknown Lab'}
                  </Typography>
                  {report.status !== 'published' && (
                    <IconButton
                      size="small"
                      onClick={() => setEditingLabName(true)}
                      sx={{
                        p: 0.25,
                        color: '#8B5CF6',
                        '&:hover': { bgcolor: 'rgba(139, 92, 246, 0.1)' }
                      }}
                    >
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </Box>
              ) : (
                <FormControl fullWidth size="small" disabled={savingLabName}>
                  <Select
                    value={report.extractedData?.labName || ''}
                    onChange={handleLabNameChange}
                    displayEmpty
                    open={editingLabName}
                    onClose={() => setEditingLabName(false)}
                    sx={{
                      fontWeight: 500,
                      color: '#111827',
                      fontSize: '13px',
                      '& .MuiSelect-select': { py: 0.25, px: 0.5 },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E5E7EB' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#8B5CF6' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#8B5CF6' }
                    }}
                  >
                    {report.extractedData?.labName && !configuredLabs.includes(report.extractedData.labName) && (
                      <MenuItem value={report.extractedData.labName}>
                        {report.extractedData.labName} (Current)
                      </MenuItem>
                    )}
                    {configuredLabs.map((labName) => (
                      <MenuItem key={labName} value={labName}>
                        {labName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </CardContent>
          </Card>
          </Tooltip>
        </Grid>

        <Grid size={{ xs: 6, md: 4 }}>
          <Tooltip title={report.extractedData?.patientGender || 'Details not available'} placement="bottom" arrow>
          <Card sx={{
            bgcolor: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: 1.5,
            boxShadow: 'none',
            height: '100%',
            minHeight: 48
          }}>
            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 500, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 0.5, color: '#06B6D4', fontSize: 14 }} />
                Patient
              </Typography>
              <Typography sx={{ fontWeight: 500, color: '#111827', fontSize: '13px', lineHeight: 1.3 }}>
                {report.extractedData?.patientName || 'Not specified'}
              </Typography>
            </CardContent>
          </Card>
          </Tooltip>
        </Grid>

        <Grid size={{ xs: 6, md: 2.5 }}>
          <Tooltip title={`${activeParams.length} parameters${excludedParams.length > 0 ? ` (${excludedParams.length} excluded)` : ''}`} placement="bottom" arrow>
          <Card sx={{
            bgcolor: 'white',
            border: '1px solid #E5E7EB',
            borderRadius: 1.5,
            boxShadow: 'none',
            height: '100%',
            minHeight: 48
          }}>
            <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 500, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 0.5, display: 'flex', alignItems: 'center' }}>
                <FlagIcon sx={{ mr: 0.5, color: getFlagColor(), fontSize: 14 }} />
                Result
              </Typography>
              <Typography sx={{ fontWeight: 500, color: getFlagColor(), fontSize: '13px', lineHeight: 1.3 }}>
                {resultLabel(report).label}
              </Typography>
            </CardContent>
          </Card>
          </Tooltip>
        </Grid>

        {/* Audit Summary Card - Only show for approved/rejected reports */}
        {report.auditSummary && (report.status === 'approved' || report.status === 'rejected') && (
          <Grid size={{ xs: 12, md: 3 }}>
            <Card sx={{
              bgcolor: report.auditSummary.accuracyPercentage >= 90 ? '#ECFDF5' : report.auditSummary.accuracyPercentage >= 70 ? '#FEF3C7' : '#FEE2E2',
              border: `1px solid ${report.auditSummary.accuracyPercentage >= 90 ? '#6EE7B7' : report.auditSummary.accuracyPercentage >= 70 ? '#FCD34D' : '#FECACA'}`,
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              height: '100%'
            }}>
              <CardContent sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                  <HistoryIcon sx={{ mr: 1, color: report.auditSummary.accuracyPercentage >= 90 ? '#10B981' : report.auditSummary.accuracyPercentage >= 70 ? '#F59E0B' : '#EF4444', fontSize: 20 }} />
                  <Typography variant="subtitle2" sx={{ fontSize: '12px', fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Accuracy
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: report.auditSummary.accuracyPercentage >= 90 ? '#065F46' : report.auditSummary.accuracyPercentage >= 70 ? '#92400E' : '#991B1B', fontSize: '24px', mb: 0.5 }}>
                  {report.auditSummary.accuracyPercentage.toFixed(1)}%
                </Typography>
                <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '12px' }}>
                  {report.auditSummary.editedParameters} of {report.auditSummary.totalParameters} edited
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Test Results Table */}
      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.12)', border: '1px solid #E5E7EB', bgcolor: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
              Test Results
            </Typography>
            {/* Current page was classified as non-results: its rows were not imported */}
            {currentPageMeta?.pageType === 'non_results' && (
              <Tooltip
                title={`Page ${currentPageMeta.pageNumber} was classified as a non-results page (cover, summary, notes or a repeated table), so its rows were not imported.`}
                arrow
                placement="top"
              >
                <Chip
                  size="small"
                  label={`page ${currentPageMeta.pageNumber} skipped${currentPageMeta.droppedRowCount ? ` · ${currentPageMeta.droppedRowCount} rows` : ''}`}
                  color="default"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '11px' }}
                />
              </Tooltip>
            )}
          </Box>
          <TextField
            size="small"
            placeholder="Search parameters..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#9CA3AF' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: '#F9FAFB',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                }
              }
            }}
          />
        </Box>

        {/* Overview of pages the classifier skipped, so the nurse does not have to page
            through the PDF to discover them (page nav itself is prev/next only) */}
        {skippedPages.length > 0 && (
          <Typography variant="caption" sx={{ display: 'block', mb: 2, color: '#6B7280' }}>
            Not lab-result pages:{' '}
            {skippedPages
              .map((p) => `page ${p.pageNumber}${p.droppedRowCount ? ` (${p.droppedRowCount} rows)` : ''}`)
              .join(', ')}
            . Rows from these pages were not imported.
          </Typography>
        )}

        {/* Excluded Parameters CTA */}
        {excludedParams.length > 0 && (
          <Alert
            severity="info"
            sx={{ mb: 2, bgcolor: '#FEF3C7', border: '1px solid #FBBF24' }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setShowExcludedParamsDialog(true)}
                sx={{ textDecoration: 'underline' }}
              >
                View ({excludedParams.length})
              </Button>
            }
          >
            <Typography variant="body2">
              {excludedParams.length} parameter{excludedParams.length !== 1 ? 's' : ''} excluded from validation
            </Typography>
          </Alert>
        )}

        {/* Phase 11: Vendor Mapping Summary Card (hidden for legacy reports) */}
        {report?.vendorMappingSummary?.processedAt && (
          <Box sx={{ mb: 2, p: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Vendor: <span style={{ color: '#4361EE' }}>{report.vendorMappingSummary.vendorName || 'NONE'}</span>
                </Typography>
                <Chip label={`Auto: ${report.vendorMappingSummary.autoMatched}`} size="small" sx={{ bgcolor: '#D1FAE5', color: '#065F46' }} />
                <Chip label={`Suggested: ${report.vendorMappingSummary.suggested}`} size="small" sx={{ bgcolor: '#FEF3C7', color: '#92400E' }} />
                <Chip label={`Confirmed: ${(report.vendorMappingSummary.nurseConfirmed || 0) + (report.vendorMappingSummary.nurseOverridden || 0)}`} size="small" sx={{ bgcolor: '#DBEAFE', color: '#1E40AF' }} />
                <Chip label={`Unmapped: ${report.vendorMappingSummary.unmapped}`} size="small" sx={{ bgcolor: '#FEE2E2', color: '#991B1B' }} />
                <Chip label={`N/A: ${report.vendorMappingSummary.notApplicable}`} size="small" sx={{ bgcolor: '#F3F4F6', color: '#6B7280' }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {report.vendorMappingSummary.suggested > 0 && (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    disabled={bulkConfirming || report.status === 'published'}
                    onClick={handleBulkConfirmMappings}
                  >
                    {bulkConfirming ? <CircularProgress size={16} color="inherit" /> : 'Confirm All Suggestions'}
                  </Button>
                )}
                <Button
                  size="small"
                  variant="outlined"
                  disabled={retriggeringMapping || report.status === 'published'}
                  onClick={handleRetriggerMapping}
                >
                  {retriggeringMapping ? <CircularProgress size={16} /> : 'Re-run Mapping'}
                </Button>
              </Box>
            </Box>
          </Box>
        )}

        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                {columnOrder.map((columnName) => (
                  <TableCell key={columnName} sx={{ fontWeight: 600, color: '#111827', fontSize: '13px', py: 2, borderBottom: '1px solid #E5E7EB' }}>
                    {columnName}
                  </TableCell>
                ))}
                {report?.vendorMappingSummary?.processedAt && (
                  <TableCell sx={{ fontWeight: 600, color: '#111827', fontSize: '13px', py: 2, borderBottom: '1px solid #E5E7EB' }}>
                    Vendor Mapping
                  </TableCell>
                )}
                <TableCell sx={{ fontWeight: 600, color: '#111827', fontSize: '13px', py: 2, textAlign: 'right', borderBottom: '1px solid #E5E7EB' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredResults?.map((parameter: TestResult) => {
                const indicator = getValueIndicator(parameter);
                const isEditing = editMode[parameter._id || ''];
                const isAbnormal = indicator?.severity === 'critical' || indicator?.severity === 'warning';

                return (
                  <TableRow
                    key={parameter._id}
                    id={`parameter-${parameter.serviceItemName.replace(/\s+/g, '-')}`}
                    sx={{
                      backgroundColor: indicator?.severity === 'critical'
                        ? 'rgba(239, 68, 68, 0.04)'
                        : indicator?.severity === 'warning'
                        ? 'rgba(245, 158, 11, 0.04)'
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: indicator?.severity === 'critical'
                          ? 'rgba(239, 68, 68, 0.08)'
                          : indicator?.severity === 'warning'
                          ? 'rgba(245, 158, 11, 0.08)'
                          : '#F9FAFB',
                      },
                      borderBottom: '1px solid #F3F4F6',
                    }}
                  >
                    {columnOrder.map((columnName) => (
                      <TableCell key={columnName} sx={{ py: 2.5, px: 2 }}>
                        {renderCellContent(columnName, parameter, isEditing, isAbnormal, indicator)}
                      </TableCell>
                    ))}
                    {report?.vendorMappingSummary?.processedAt && (
                      <TableCell sx={{ py: 2.5, px: 2, minWidth: 220 }}>
                        {(() => {
                          // For per-page rendered rows, parameter may lack vendorMapping.
                          // Look it up by (serviceItemName, value) against the top-level
                          // results so the chips appear consistently across both rendering modes.
                          const directVm = (parameter as any).vendorMapping;
                          const lookupKey = `${parameter.serviceItemName ?? ''}|${parameter.value ?? ''}`;
                          const fallback = vendorMappingByKey.get(lookupKey);
                          // A row known exactly (no page data, or linked by rowKey) uses its own results
                          // row and sends its ids; an unlinked page row keeps the lookup above
                          const exact = mappingRowRef(report?.extractedData, parameter, currentPage);
                          const effectiveVm = exact ? exact.resultRow.vendorMapping ?? directVm : directVm || fallback?.mapping;
                          const idx = exact
                            ? exact.resultIndex
                            : (resultIndexById.get(String(parameter._id)) ?? -1) >= 0
                            ? (resultIndexById.get(String(parameter._id)) as number)
                            : (fallback?.resultIndex ?? -1);
                          return (
                            <MappingStatusChip
                              reportId={id || ''}
                              resultIndex={idx}
                              rowIds={exact?.ids}
                              vendorMapping={effectiveVm}
                              originalExtractedName={parameter.serviceItemName}
                              unitFromRow={parameter.unit}
                              vendorName={report?.vendorMappingSummary?.vendorName}
                              onUpdated={(newMapping, newSummary) => {
                                if (idx >= 0) handleMappingUpdated(idx, newMapping, newSummary);
                              }}
                            />
                          );
                        })()}
                      </TableCell>
                    )}
                    <TableCell sx={{ py: 2.5, px: 2, textAlign: 'right' }}>
                      {isEditing ? (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleSaveEdit(parameter._id || '')}
                            disabled={saving}
                            title="Save changes"
                            sx={{
                              bgcolor: '#4361EE',
                              color: 'white',
                              '&:hover': { bgcolor: '#3651CE' },
                              width: 32,
                              height: 32
                            }}
                          >
                            <SaveIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setEditMode(prev => ({ ...prev, [parameter._id || '']: false }))}
                            title="Cancel editing"
                            sx={{
                              bgcolor: '#F3F4F6',
                              color: '#6B7280',
                              '&:hover': { bgcolor: '#E5E7EB' },
                              width: 32,
                              height: 32
                            }}
                          >
                            <CancelIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          {/* Check if this parameter has validation flags */}
                          {(() => {
                            const validationFlag = report?.validationFlags?.find(
                              flag => flag.parameterName === parameter.serviceItemName &&
                                     !flag.isExcluded // Don't show buttons for excluded parameters
                            );

                            if (validationFlag && (validationFlag.flagType === 'PARAMETER_NOT_FOUND' ||
                                                   validationFlag.flagType === 'UNIT_MISMATCH')) {
                              return (
                                <>
                                  <Tooltip title="Add to Parameter Master">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleAddToParameterMaster(parameter, validationFlag)}
                                      sx={{
                                        color: '#10B981',
                                        '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.08)' },
                                        width: 32,
                                        height: 32
                                      }}
                                    >
                                      <ParameterMasterIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Add to Exclusion List">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleAddToExclusion(parameter, validationFlag)}
                                      sx={{
                                        color: '#F59E0B',
                                        '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.08)' },
                                        width: 32,
                                        height: 32
                                      }}
                                    >
                                      <BlockIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              );
                            }
                            return null;
                          })()}

                          <IconButton
                            size="small"
                            onClick={() => handleEditToggle(parameter._id || '')}
                            title={report.status === 'published' ? 'Cannot edit a published report' : 'Edit parameter'}
                            disabled={report.status === 'published'}
                            sx={{
                              color: report.status === 'published' ? '#D1D5DB' : '#6B7280',
                              '&:hover': { bgcolor: '#F3F4F6', color: '#4361EE' },
                              width: 32,
                              height: 32
                            }}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteClick(parameter._id || '', parameter.serviceItemName)}
                            title={report.status === 'published' ? 'Cannot delete from a published report' : 'Delete parameter'}
                            disabled={report.status === 'published'}
                            sx={{
                              color: report.status === 'published' ? '#D1D5DB' : '#EF4444',
                              '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.08)' },
                              width: 32,
                              height: 32
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/nurse/reports')}
          sx={{
            borderColor: '#D1D5DB',
            color: '#6B7280',
            '&:hover': {
              borderColor: '#9CA3AF',
              bgcolor: '#F9FAFB'
            },
            borderRadius: 2,
            textTransform: 'none',
            px: 3,
            py: 1
          }}
        >
          Back to Reports
        </Button>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {!report.orderId && (
            <Alert
              severity="warning"
              sx={{
                mr: 2,
                borderRadius: 2,
                border: '1px solid #FCD34D',
                bgcolor: '#FEF3C7',
                '& .MuiAlert-icon': { color: '#F59E0B' }
              }}
            >
              <Typography variant="body2" sx={{ fontSize: '14px', color: '#92400E' }}>
                Please add an Order ID before approving or rejecting this report
              </Typography>
            </Alert>
          )}
          <Tooltip title={report.status === 'published' ? 'Cannot modify a published report' : 'Re-validate all parameters against Parameter Master database'}>
            <Button
              variant="outlined"
              startIcon={revalidating ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRevalidate}
              disabled={revalidating || !report.extractedData || report.status === 'published'}
              sx={{
                borderColor: '#3B82F6',
                color: '#3B82F6',
                '&:hover': {
                  borderColor: '#2563EB',
                  bgcolor: 'rgba(59, 130, 246, 0.04)'
                },
                '&:disabled': {
                  borderColor: '#E5E7EB',
                  color: '#9CA3AF'
                },
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                py: 1
              }}
            >
              {revalidating ? 'Re-validating...' : 'Re-validate Parameters'}
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => setShowRejectDialog(true)}
            disabled={!report.orderId || report.status === 'published'}
            title={report.status === 'published' ? 'Cannot reject a published report' : (!report.orderId ? 'Order ID required' : '')}
            sx={{
              borderColor: '#FCA5A5',
              color: '#EF4444',
              '&:hover': {
                borderColor: '#EF4444',
                bgcolor: 'rgba(239, 68, 68, 0.04)'
              },
              '&:disabled': {
                borderColor: '#E5E7EB',
                color: '#9CA3AF'
              },
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              py: 1
            }}
          >
            Reject Report
          </Button>
          <Button
            variant="contained"
            startIcon={<ApproveIcon />}
            onClick={() => fetchValidationData()}
            disabled={!report.orderId || loadingValidation || report.status === 'published'}
            title={report.status === 'published' ? 'Cannot modify a published report' : (!report.orderId ? 'Order ID required' : '')}
            sx={{
              bgcolor: '#10B981',
              color: 'white',
              '&:hover': {
                bgcolor: '#059669'
              },
              '&:disabled': {
                bgcolor: '#E5E7EB',
                color: '#9CA3AF'
              },
              borderRadius: 2,
              textTransform: 'none',
              px: 3,
              py: 1,
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
            }}
          >
            {showRepeatReview ? 'Re-Approve Report' : 'Approve Report'}
          </Button>
        </Box>
      </Box>

      {/* Approve Dialog with Validation */}
      <Dialog
        open={showApproveDialog}
        onClose={() => {
          setShowApproveDialog(false);
          setApprovalNotes('');
          setMismatchReason('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          bgcolor: hasValidationWarnings ? '#FEF3C7' : '#D1FAE5',
          borderBottom: `2px solid ${hasValidationWarnings ? '#F59E0B' : '#10B981'}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {hasValidationWarnings ? (
              <WarningIcon sx={{ color: '#F59E0B', mr: 1 }} />
            ) : (
              <CheckIcon sx={{ color: '#10B981', mr: 1 }} />
            )}
            <Typography variant="h6">
              {hasValidationWarnings ? 'Validation Warnings Detected' : 'Ready to Approve'}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {/* Validation Status Summary */}
          {validationWarnings.some(w => w.type === 'ORDER_NOT_FOUND') ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Order ID Not Found
              </Typography>
              <Typography variant="body2">
                {validationWarnings.find(w => w.type === 'ORDER_NOT_FOUND')?.message ||
                 'The Order ID could not be found in the database. Please verify the Order ID is correct before proceeding.'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                This report cannot be approved until the Order ID issue is resolved.
              </Typography>
            </Alert>
          ) : hasValidationWarnings ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The following mismatches were detected between Order data and LLM-extracted data.
              Please review and confirm before approving.
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2 }}>
              All patient demographic fields match between Order data and LLM extraction.
              You can proceed with approval.
            </Alert>
          )}

          {/* Side-by-side Comparison - Only show if not ORDER_NOT_FOUND */}
          {!validationWarnings.some(w => w.type === 'ORDER_NOT_FOUND') && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Data Comparison
              </Typography>

              {/* Patient Age */}
              <Card sx={{ mb: 2, border: `1px solid ${
                validationWarnings.some(w => w.field === 'patientAge') ? '#FCD34D' : '#D1FAE5'
              }`, bgcolor: validationWarnings.some(w => w.field === 'patientAge') ? '#FFFBEB' : '#F0FDF4' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Patient Age
                    </Typography>
                    {validationWarnings.some(w => w.field === 'patientAge') ? (
                      <Chip icon={<CloseIcon />} label="Mismatch" color="warning" size="small" />
                    ) : (
                      <Chip icon={<CheckIcon />} label="Match" color="success" size="small" />
                    )}
                  </Box>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Observation Data:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {orderData?.patient_age || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">LLM Extracted:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {reportData?.patientAge || 'Not specified'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Patient Gender */}
              <Card sx={{ mb: 2, border: `1px solid ${
                validationWarnings.some(w => w.field === 'patientGender') ? '#FCD34D' : '#D1FAE5'
              }`, bgcolor: validationWarnings.some(w => w.field === 'patientGender') ? '#FFFBEB' : '#F0FDF4' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Patient Gender
                    </Typography>
                    {validationWarnings.some(w => w.field === 'patientGender') ? (
                      <Chip icon={<CloseIcon />} label="Mismatch" color="warning" size="small" />
                    ) : (
                      <Chip icon={<CheckIcon />} label="Match" color="success" size="small" />
                    )}
                  </Box>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Observation Data:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {orderData?.gender || orderData?.patient_gender || 'Not specified'}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">LLM Extracted:</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {reportData?.patientGender || 'Not specified'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* Override Reason (only if warnings and NOT ORDER_NOT_FOUND) */}
          {hasValidationWarnings && !validationWarnings.some(w => w.type === 'ORDER_NOT_FOUND') && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Reason for Override (Required)"
              value={mismatchReason}
              onChange={(e) => setMismatchReason(e.target.value)}
              placeholder="Please explain why you are approving despite these mismatches..."
              required
              sx={{ mb: 2 }}
            />
          )}

          {/* Approval Notes - Only show if not ORDER_NOT_FOUND */}
          {!validationWarnings.some(w => w.type === 'ORDER_NOT_FOUND') && (
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Notes (Optional)"
              value={approvalNotes}
              onChange={(e) => setApprovalNotes(e.target.value)}
              placeholder="Add any notes about this approval..."
            />
          )}

          {hasValidationWarnings && !validationWarnings.some(w => w.type === 'ORDER_NOT_FOUND') && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                By proceeding, you acknowledge these mismatches and confirm that the approval is correct.
                This action will be recorded in the audit trail.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: '#F9FAFB' }}>
          <Button
            onClick={() => {
              setShowApproveDialog(false);
              setApprovalNotes('');
              setMismatchReason('');
            }}
            sx={{ mr: 1 }}
          >
            {validationWarnings.some(w => w.type === 'ORDER_NOT_FOUND') ? 'Close' : 'Cancel'}
          </Button>
          {/* Hide Approve button if ORDER_NOT_FOUND */}
          {!validationWarnings.some(w => w.type === 'ORDER_NOT_FOUND') && (
            <Button
              variant="contained"
              color={hasValidationWarnings ? 'warning' : 'success'}
              onClick={() => {
                if (hasValidationWarnings && !mismatchReason.trim()) {
                  enqueueSnackbar('Please provide a reason for override', { variant: 'warning' });
                  return;
                }
                handleApprove(hasValidationWarnings, mismatchReason);
              }}
              disabled={hasValidationWarnings && !mismatchReason.trim()}
              startIcon={<ApproveIcon />}
            >
              {hasValidationWarnings ? 'Approve with Override' : 'Confirm Approval'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onClose={() => setShowRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Report</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this report.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Explain why this report is being rejected..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRejectDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleReject}>
            Confirm Rejection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog open={showPublishDialog} onClose={() => setShowPublishDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PublishIcon sx={{ color: '#10B981' }} />
          Confirm Publish
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Are you sure you want to publish this data?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Once published, you won't be able to update this report.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowPublishDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => handlePublish(false)}
            startIcon={<PublishIcon />}
          >
            Publish
          </Button>
        </DialogActions>
      </Dialog>

      {/* Phase 9: Vendor Mapping Gate Dialog (also needed in main view) */}
      <Dialog open={showMappingGateDialog} onClose={() => setShowMappingGateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#92400E' }}>
          <PublishIcon sx={{ color: '#F59E0B' }} />
          Incomplete Vendor Mapping
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Some parameters don't have a confirmed vendor mapping. They will be published with their original extracted names.
          </Typography>
          {mappingGateData.unconfirmedSuggestions.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#92400E' }}>
                {mappingGateData.unconfirmedSuggestions.length} unconfirmed suggestion(s):
              </Typography>
              <ul style={{ marginTop: 4, paddingLeft: 20 }}>
                {mappingGateData.unconfirmedSuggestions.slice(0, 10).map((s, i) => (
                  <li key={i} style={{ fontSize: 13 }}>
                    <strong>{s.serviceItemName}</strong> → suggested: <em>{s.suggestedName}</em> ({s.confidence}%)
                  </li>
                ))}
                {mappingGateData.unconfirmedSuggestions.length > 10 && (
                  <li style={{ fontSize: 13, opacity: 0.7 }}>...and {mappingGateData.unconfirmedSuggestions.length - 10} more</li>
                )}
              </ul>
            </Box>
          )}
          {mappingGateData.unmappedParameters.length > 0 && (
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#991B1B' }}>
                {mappingGateData.unmappedParameters.length} unmapped parameter(s):
              </Typography>
              <ul style={{ marginTop: 4, paddingLeft: 20 }}>
                {mappingGateData.unmappedParameters.slice(0, 10).map((p, i) => (
                  <li key={i} style={{ fontSize: 13 }}>{p.serviceItemName}</li>
                ))}
                {mappingGateData.unmappedParameters.length > 10 && (
                  <li style={{ fontSize: 13, opacity: 0.7 }}>...and {mappingGateData.unmappedParameters.length - 10} more</li>
                )}
              </ul>
            </Box>
          )}
          <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
            Click "Publish Anyway" to publish with the original names, or "Cancel" to go back and confirm/correct mappings.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowMappingGateDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => { setShowMappingGateDialog(false); handlePublish(true); }}
            startIcon={<PublishIcon />}
          >
            Publish Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order-condition warning — must exist in BOTH render branches. The
          nurse can flip to the repeat-review view while a publish is in
          flight; a gate that sets state with no dialog mounted here would
          silently do nothing. */}
      <Dialog open={showOrderConditionsDialog} onClose={() => setShowOrderConditionsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#B45309' }}>
          <WarningIcon /> Check before publishing
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This report can be published, but the HIMS order looks unusual:
          </Typography>
          <ul style={{ marginTop: 4, paddingLeft: 20 }}>
            {orderConditionWarnings.map((w, i) => (
              <li key={i} style={{ fontSize: 13, marginBottom: 4 }}>{w}</li>
            ))}
          </ul>
          <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
            The test results themselves are unaffected and will be sent correctly.
            Publish anyway, or cancel if this looks like the wrong order.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowOrderConditionsDialog(false)} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => { setShowOrderConditionsDialog(false); handlePublish(false, undefined, true); }}
            startIcon={<PublishIcon />}
          >
            Publish Anyway
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit History Dialog */}
      <Dialog open={showHistory} onClose={() => setShowHistory(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit History</DialogTitle>
        <DialogContent>
          {/* Show approval info if report is approved */}
          {report.status === 'approved' && report.approvedBy && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Approved by</strong> {report.approvedBy.name || report.approvedBy.email}, {report.approvedAt ? new Date(report.approvedAt).toLocaleString() : 'N/A'}
              </Typography>
            </Alert>
          )}
          {report.editHistory && report.editHistory.length > 0 ? (
            <List>
              {report.editHistory.map((edit, index) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <EditIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${edit.field}: ${edit.originalValue} → ${edit.newValue}`}
                    secondary={`Edited by ${edit.editedBy?.name || 'Unknown'} on ${new Date(edit.editedAt).toLocaleString()}${edit.reason ? ` - ${edit.reason}` : ''}`}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No edits have been made to this report
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHistory(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Parameter Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => !deleting && setShowDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Parameter</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Warning: This action cannot be undone
            </Typography>
          </Alert>
          <Typography variant="body2">
            Are you sure you want to delete the following parameter?
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, fontWeight: 600 }}>
            {parameterToDelete?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will permanently remove this test result from the report.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowDeleteDialog(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteParameter}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete Parameter'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add to Parameter Master Dialog */}
      <Dialog
        open={showParameterMasterDialog}
        onClose={() => !submittingSuggestion && setShowParameterMasterDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#E0F2FE', borderBottom: '2px solid #0284C7' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ParameterMasterIcon sx={{ mr: 1, color: '#0284C7' }} />
            Add to Parameter Master
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box sx={{ mb: 2, p: 2, bgcolor: '#F9FAFB', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">Current Parameter:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {selectedParameter?.serviceItemName}
            </Typography>
            {selectedParameter?.unit && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Unit:</Typography>
                <Typography variant="body1">{selectedParameter.unit}</Typography>
              </>
            )}
          </Box>

          {/* Action Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
              Choose Action:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant={parameterAction === 'create' ? 'contained' : 'outlined'}
                onClick={() => {
                  setParameterAction('create');
                  setTargetParameter(null);
                }}
                sx={{ flex: 1 }}
              >
                Create New Parameter
              </Button>
              <Button
                variant={parameterAction === 'update_alias' ? 'contained' : 'outlined'}
                onClick={() => {
                  setParameterAction('update_alias');
                  setNewParameterId('');
                  setValueType('numeric');
                  setParameterDescription('');
                }}
                sx={{ flex: 1 }}
              >
                Add as Alias to Existing
              </Button>
            </Box>
          </Box>

          {/* Create New Parameter Form */}
          {parameterAction === 'create' && (
            <Box>
              <TextField
                fullWidth
                label="Parameter ID (Key)"
                placeholder="e.g., HEMOGLOBIN, WBC_COUNT"
                value={newParameterId}
                onChange={(e) => setNewParameterId(e.target.value.toUpperCase())}
                required
                sx={{ mb: 2 }}
                helperText="Unique identifier for the parameter (uppercase, no spaces)"
              />

              <TextField
                select
                fullWidth
                label="Value Type"
                value={valueType}
                onChange={(e) => setValueType(e.target.value as any)}
                required
                SelectProps={{ native: true }}
                sx={{ mb: 2 }}
              >
                <option value="numeric">Numeric</option>
                <option value="text">Text</option>
                <option value="alphanumeric">Alphanumeric</option>
                <option value="range">Range</option>
              </TextField>

              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description (Optional)"
                placeholder="Brief description of the parameter"
                value={parameterDescription}
                onChange={(e) => setParameterDescription(e.target.value)}
                sx={{ mb: 2 }}
              />

              {selectedParameter?.unit && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    The unit "{selectedParameter.unit}" will be added as a possible unit for this parameter.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}

          {/* Add as Alias Form */}
          {parameterAction === 'update_alias' && (
            <Box>
              <ParameterSearchField
                value={targetParameter}
                onChange={setTargetParameter}
                label="Search and Select Target Parameter"
                placeholder="Type to search existing parameters..."
                required
                helperText="Select the parameter to which this will be added as an alias"
              />

              {targetParameter && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#F0F9FF', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="primary">Selected Parameter:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {targetParameter.parameterName} ({targetParameter.parameterId})
                  </Typography>
                  {targetParameter.aliases?.length > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Current aliases: {targetParameter.aliases.join(', ')}
                    </Typography>
                  )}
                  {selectedValidationFlag?.flagType === 'UNIT_MISMATCH' && selectedParameter?.unit && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        The unit "{selectedParameter.unit}" will also be added to this parameter's possible units.
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}
            </Box>
          )}

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Your suggestion will be queued for admin approval. Once approved, it will be automatically applied to the Parameter Master.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowParameterMasterDialog(false)}
            disabled={submittingSuggestion}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleParameterMasterSubmit}
            disabled={
              submittingSuggestion ||
              (parameterAction === 'create' && !newParameterId.trim()) ||
              (parameterAction === 'update_alias' && !targetParameter)
            }
            startIcon={submittingSuggestion ? <CircularProgress size={16} /> : <AddIcon />}
            sx={{ bgcolor: '#0284C7', '&:hover': { bgcolor: '#0369A1' } }}
          >
            {submittingSuggestion ? 'Submitting...' : 'Submit Suggestion'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Suggest Exclusion Dialog */}
      <Dialog
        open={showExclusionDialog}
        onClose={() => setShowExclusionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#FEF3C7', borderBottom: '2px solid #F59E0B' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BlockIcon sx={{ mr: 1, color: '#F59E0B' }} />
            Suggest Parameter Exclusion
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Your suggestion will be sent to an administrator for approval.
          </Alert>
          <Typography variant="body2" gutterBottom>
            Suggest this parameter for exclusion to skip validation in future reports.
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: '#F9FAFB', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">Parameter Name:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {selectedParameter?.serviceItemName}
            </Typography>
            {selectedValidationFlag?.flagType === 'UNIT_MISMATCH' && (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Unit:</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {selectedParameter?.unit || '-'}
                </Typography>
              </>
            )}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Lab:</Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {report?.extractedData?.labName || 'All Labs'}
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason for Exclusion"
            placeholder="Enter the reason for excluding this parameter..."
            sx={{ mt: 2 }}
            onChange={(e) => setMismatchReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExclusionDialog(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<BlockIcon />}
            onClick={() => handleExclusionSubmit({
              parameterName: selectedParameter?.serviceItemName,
              unit: selectedValidationFlag?.flagType === 'UNIT_MISMATCH' ? selectedParameter?.unit : null,
              reason: mismatchReason
            })}
            disabled={!mismatchReason.trim()}
            sx={{ bgcolor: '#F59E0B', '&:hover': { bgcolor: '#D97706' } }}
          >
            Submit Suggestion
          </Button>
        </DialogActions>
      </Dialog>

      {/* Excluded Parameters Dialog */}
      <Dialog
        open={showExcludedParamsDialog}
        onClose={() => setShowExcludedParamsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BlockIcon sx={{ mr: 1, color: '#6B7280' }} />
            Excluded Parameters
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These parameters are excluded from validation and do not appear in the main review table.
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Parameter</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Exclusion Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {excludedParams.map((param: TestResult, idx: number) => {
                  const flag = report?.validationFlags?.find(
                    (f: any) =>
                      f.parameterName === param.serviceItemName &&
                      f.isExcluded === true
                  );
                  return (
                    <TableRow key={idx}>
                      <TableCell>{param.serviceItemName}</TableCell>
                      <TableCell>{param.value}</TableCell>
                      <TableCell>{param.unit}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {flag?.exclusionReason || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExcludedParamsDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default ReviewReport;